import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Eye, EyeOff, ArrowUp, ArrowDown, ImagePlus, X as XIcon, GripVertical, Monitor, Tablet, Smartphone, Save, Send, RotateCcw, XCircle, Star as StarIcon } from 'lucide-react';
import { API, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate, printArea, PrintStyle, StatusBadge, Paginator, FinanceEmpty, PageHead, exportPdf, exportImage } from './organization/finance/shared';
import Icon from '../components/dashboard/DashboardIcons';
import { ICON_OPTIONS } from '../components/landing/iconMap';
import { DaysBar, daysRemaining, AdminWhatsAppModal } from './superadmin/subscriptionWidgets';
import { ActivityCell } from './superadmin/presence';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../lib/whatsapp';

const sauth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const ROLE_LABELS = { super_admin: 'سوبر أدمن', admin: 'مدير النظام', manager: 'إداري', treasurer: 'أمين صندوق', coach: 'مدرب', analyst: 'محلل', secretary: 'سكرتير', supervisor: 'مشرف', viewer: 'مشاهد' };
const PLAN_LABELS = { free: 'مجانية', basic: 'أساسية', premium: 'مميزة', enterprise: 'مؤسسات' };
const SUB_STATUS = { active: ['نشط', 'bg-teal-50 text-teal-700'], trial: ['تجريبي', 'bg-amber-50 text-amber-700'], expired: ['منتهٍ', 'bg-red-50 text-red-600'], cancelled: ['ملغى', 'bg-slate-100 text-slate-500'], pending: ['معلق', 'bg-blue-50 text-blue-700'] };

// ====================== الواجهة الرئيسية (Landing) ======================
const SECTION_LABELS = { navbar: 'الشريط العلوي', hero: 'القسم الرئيسي (Hero)', stats: 'الإحصائيات', audiences: 'لمن تسيار؟ (الأدوار الأربعة)', features: 'المميزات', showcase: 'جولة داخل المنصة', how_it_works: 'كيف تعمل؟', migration: 'نقل البيانات من Excel', testimonials: 'آراء العملاء', faq: 'الأسئلة الشائعة', coaches_banner: 'شريط المدربين', partners: 'الشركاء', pricing: 'الباقات', cta: 'دعوة للعمل (CTA)', footer: 'التذييل والتواصل' };
const DEFAULT_SECTIONS = [
  { key: 'navbar', visible: true, order: 1 }, { key: 'hero', visible: true, order: 2 },
  { key: 'stats', visible: true, order: 3 }, { key: 'audiences', visible: true, order: 3.5 },
  { key: 'features', visible: true, order: 4 }, { key: 'showcase', visible: true, order: 4.5 },
  { key: 'how_it_works', visible: true, order: 5 }, { key: 'migration', visible: true, order: 5.5 },
  { key: 'testimonials', visible: true, order: 6 }, { key: 'faq', visible: true, order: 6.5 },
  { key: 'cta', visible: true, order: 7 }, { key: 'coaches_banner', visible: true, order: 8 },
  { key: 'footer', visible: true, order: 9 },
  { key: 'partners', visible: false, order: 10 }, { key: 'pricing', visible: false, order: 11 },
];
const DEFAULT_STATS_ITEMS = [
  { key: 'active_clubs', label: 'منشأة رياضية نشطة', visible: true, order: 1 },
  { key: 'academies_count', label: 'أكاديمية مسجلة', visible: true, order: 2 },
  { key: 'active_coaches', label: 'مدرب نشط بالمنصة', visible: true, order: 3 },
  { key: 'total_players', label: 'لاعب يُدار عبر المنصة', visible: true, order: 4 },
];
const PREVIEW_DEVICES = { desktop: 1280, tablet: 768, mobile: 390 };

const Card = ({ title, children, note }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="mb-3 text-sm font-bold text-slate-800">{title}</div>
    {note && <p className="mb-3 text-xs text-slate-400">{note}</p>}
    <div className="space-y-3">{children}</div>
  </div>
);

const SectionsManager = ({ sections, setSections }) => {
  const sorted = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const dragFrom = useRef(null);

  const onDrop = (dropIdx) => {
    const from = dragFrom.current;
    if (from === null || from === dropIdx) return;
    const next = [...sorted];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    setSections(next.map((s, i) => ({ ...s, order: i + 1 })));
    dragFrom.current = null;
  };
  const toggle = (key) => setSections(sorted.map((s) => (s.key === key ? { ...s, visible: s.visible === false } : s)));

  return (
    <Card title="ترتيب أقسام الصفحة وإظهارها/إخفاؤها" note="اسحب أي قسم بمقبض النقل لإعادة ترتيبه، أو أخفِه مؤقتاً دون حذف محتواه.">
      <div className="space-y-1.5" data-testid="landing-sections-manager">
        {sorted.map((s, i) => (
          <div
            key={s.key}
            draggable={s.key !== 'navbar' && s.key !== 'footer'}
            onDragStart={() => { dragFrom.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className={`flex items-center justify-between rounded-lg border px-3 py-2 ${s.key === 'navbar' || s.key === 'footer' ? 'cursor-default' : 'cursor-move'} ${s.visible === false ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-teal-100 bg-teal-50/40'}`}
            data-testid={`landing-section-row-${s.key}`}
          >
            <div className="flex items-center gap-2">
              <GripVertical className={`h-4 w-4 ${s.key === 'navbar' || s.key === 'footer' ? 'text-slate-200' : 'text-slate-400'}`} data-testid={`landing-section-drag-${s.key}`} />
              <span className="text-sm font-semibold text-slate-800">{SECTION_LABELS[s.key] || s.key}</span>
              {(s.key === 'navbar' || s.key === 'footer') && <span className="text-[10px] text-slate-400">(ثابت الموضع دائماً)</span>}
            </div>
            <button type="button" onClick={() => toggle(s.key)} className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold ${s.visible === false ? 'bg-slate-200 text-slate-600' : 'bg-teal-600 text-white'}`} data-testid={`section-toggle-${s.key}`}>
              {s.visible === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {s.visible === false ? 'مخفي' : 'ظاهر'}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
};

const FeaturesEditor = ({ form, setForm }) => {
  const items = form.features || [];
  const update = (i, key, val) => setForm({ ...form, features: items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)) });
  const remove = (i) => setForm({ ...form, features: items.filter((_, idx) => idx !== i) });
  const add = () => setForm({ ...form, features: [...items, { icon: 'star', title: '', description: '' }] });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, features: next });
  };

  return (
    <Card title="قسم المميزات (Features)">
      <Field label="العنوان"><input className={inputCls} value={form.features_title || ''} onChange={(e) => setForm({ ...form, features_title: e.target.value })} data-testid="landing-features-title-input" /></Field>
      <Field label="النص الفرعي"><input className={inputCls} value={form.features_subtitle || ''} onChange={(e) => setForm({ ...form, features_subtitle: e.target.value })} data-testid="landing-features-subtitle-input" /></Field>
      <div className="space-y-2" data-testid="landing-features-list">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`landing-feature-row-${i}`}>
            <div className="flex items-center gap-2">
              <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" value={it.icon || 'star'} onChange={(e) => update(i, 'icon', e.target.value)} data-testid={`landing-feature-icon-${i}`}>
                {ICON_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <input className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" placeholder="عنوان الميزة" value={it.title || ''} onChange={(e) => update(i, 'title', e.target.value)} data-testid={`landing-feature-title-${i}`} />
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(i)} className="rounded p-1 text-red-400 hover:bg-red-50" data-testid={`landing-feature-remove-${i}`}><Icon name="trash" className="h-3.5 w-3.5" /></button>
            </div>
            <textarea rows={2} className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm" placeholder="الوصف" value={it.description || ''} onChange={(e) => update(i, 'description', e.target.value)} data-testid={`landing-feature-desc-${i}`} />
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700" data-testid="landing-feature-add-btn">
        <Icon name="plus" className="h-4 w-4" /> إضافة ميزة
      </button>
    </Card>
  );
};

const StatsItemsEditor = ({ form, setForm, liveStats }) => {
  const items = form.stats_items?.length ? form.stats_items : DEFAULT_STATS_ITEMS;
  const sorted = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const update = (key, field, val) => setForm({ ...form, stats_items: sorted.map((it) => (it.key === key ? { ...it, [field]: val } : it)) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    const next = [...sorted];
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, stats_items: next.map((s, idx) => ({ ...s, order: idx + 1 })) });
  };

  return (
    <Card title="قسم الإحصائيات" note="القيم تُحسب آلياً من بيانات النظام الحقيقية — يمكنك تعديل التسمية والترتيب والإظهار فقط.">
      <div className="space-y-2" data-testid="landing-stats-items">
        {sorted.map((it, i) => (
          <div key={it.key} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2.5" data-testid={`landing-stat-item-${it.key}`}>
            <div className="w-16 shrink-0 text-center">
              <div className="text-base font-black text-teal-700">{liveStats?.[it.key] ?? '—'}</div>
              <div className="text-[9px] text-slate-400">القيمة الحالية</div>
            </div>
            <input className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" value={it.label || ''} onChange={(e) => update(it.key, 'label', e.target.value)} data-testid={`landing-stat-label-${it.key}`} />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === sorted.length - 1} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => update(it.key, 'visible', it.visible === false)} className={`shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold ${it.visible === false ? 'bg-slate-200 text-slate-600' : 'bg-teal-600 text-white'}`} data-testid={`landing-stat-toggle-${it.key}`}>
              {it.visible === false ? 'مخفي' : 'ظاهر'}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
};

const HowItWorksEditor = ({ form, setForm }) => {
  const steps = form.how_it_works_steps?.length ? [...form.how_it_works_steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : [];
  const update = (i, field, val) => setForm({ ...form, how_it_works_steps: steps.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, how_it_works_steps: next.map((s, idx) => ({ ...s, order: idx + 1 })) });
  };
  const add = () => setForm({ ...form, how_it_works_steps: [...steps, { title: '', description: '', order: steps.length + 1 }] });
  const remove = (i) => setForm({ ...form, how_it_works_steps: steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })) });

  return (
    <Card title="قسم كيف تعمل تسيار؟">
      <Field label="العنوان"><input className={inputCls} value={form.how_it_works_title || ''} onChange={(e) => setForm({ ...form, how_it_works_title: e.target.value })} data-testid="landing-how-it-works-title-input" /></Field>
      <Field label="النص الفرعي"><input className={inputCls} value={form.how_it_works_subtitle || ''} onChange={(e) => setForm({ ...form, how_it_works_subtitle: e.target.value })} data-testid="landing-how-it-works-subtitle-input" /></Field>
      <div className="space-y-2" data-testid="landing-how-it-works-steps">
        {steps.map((s, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`landing-step-row-${i}`}>
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-600 text-xs font-bold text-white">{i + 1}</span>
              <input className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" placeholder="عنوان الخطوة" value={s.title || ''} onChange={(e) => update(i, 'title', e.target.value)} data-testid={`landing-step-title-${i}`} />
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(i)} className="rounded p-1 text-red-400 hover:bg-red-50" data-testid={`landing-step-remove-${i}`}><Icon name="trash" className="h-3.5 w-3.5" /></button>
            </div>
            <textarea rows={2} className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm" placeholder="الوصف" value={s.description || ''} onChange={(e) => update(i, 'description', e.target.value)} data-testid={`landing-step-desc-${i}`} />
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700" data-testid="landing-step-add-btn">
        <Icon name="plus" className="h-4 w-4" /> إضافة خطوة
      </button>
    </Card>
  );
};

const LandingPreviewPane = ({ form }) => {
  const [device, setDevice] = useState('desktop');
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.4);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const compute = () => setScale(Math.min(1, el.clientWidth / PREVIEW_DEVICES[device]));
    compute();
    const obs = new ResizeObserver(compute);
    obs.observe(el);
    return () => obs.disconnect();
  }, [device]);

  const sendPreview = useCallback(() => {
    try { iframeRef.current?.contentWindow?.postMessage({ type: 'TASYAR_LANDING_PREVIEW', payload: form }, window.location.origin); } catch { /* noop */ }
  }, [form]);

  useEffect(() => {
    const t = setTimeout(sendPreview, 250);
    return () => clearTimeout(t);
  }, [form, sendPreview]);

  useEffect(() => {
    const handler = (e) => { if (e.data?.type === 'TASYAR_LANDING_PREVIEW_READY') sendPreview(); };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendPreview]);

  const width = PREVIEW_DEVICES[device];
  return (
    <div className="sticky top-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="landing-preview-pane">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold text-slate-600">معاينة مباشرة</span>
        <div className="flex gap-1">
          {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([key, DIcon]) => (
            <button key={key} type="button" onClick={() => setDevice(key)} className={`rounded-lg p-1.5 ${device === key ? 'bg-teal-600 text-white' : 'border border-slate-200 bg-white text-slate-500'}`} data-testid={`landing-preview-device-${key}`}>
              <DIcon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>
      <div ref={wrapRef} dir="ltr" className="mx-auto overflow-hidden rounded-lg border border-slate-300 bg-white" style={{ height: 620 }}>
        <div style={{ width, height: 620 / scale, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <iframe
            ref={iframeRef}
            src="/?landing_preview=1"
            title="معاينة الصفحة الرئيسية"
            onLoad={sendPreview}
            style={{ width, height: 2800, border: 'none' }}
            data-testid="landing-preview-iframe"
          />
        </div>
      </div>
    </div>
  );
};

export function LandingTab({ notify }) {
  const [form, setForm] = useState(null);
  const [published, setPublished] = useState(null);
  const [meta, setMeta] = useState({ has_unpublished_changes: false, can_revert: false, published_at: null, published_by: null });
  const [liveStats, setLiveStats] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingOg, setUploadingOg] = useState(false);

  const load = useCallback(async () => {
    try {
      const [editorRes, statsRes] = await Promise.all([axios.get(`${API}/saas/landing-page/editor`, sauth()), axios.get(`${API}/saas/public-stats`)]);
      const d = editorRes.data;
      const savedSections = d.draft.sections?.length
        ? [...d.draft.sections, ...DEFAULT_SECTIONS.filter((def) => !d.draft.sections.some((s) => s.key === def.key))]
        : DEFAULT_SECTIONS;
      setForm({ ...d.draft, sections: savedSections });
      setPublished(d.published);
      setMeta({ has_unpublished_changes: d.has_unpublished_changes, can_revert: d.can_revert, published_at: d.published_at, published_by: d.published_by });
      setLiveStats(statsRes.data);
    } catch {
      setForm({ sections: DEFAULT_SECTIONS, stats_items: DEFAULT_STATS_ITEMS });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadHeroBg = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      setForm((f) => ({ ...f, hero_background_image: res.data?.file_id }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الصورة')); }
    setUploading(false);
  };

  const uploadOgImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingOg(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      setForm((f) => ({ ...f, seo_og_image: res.data?.file_id }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع صورة المشاركة')); }
    setUploadingOg(false);
  };

  const buildPayload = () => ({
    sections: form.sections,
    hero_badge_text: form.hero_badge_text || '', hero_title: form.hero_title || '', hero_subtitle: form.hero_subtitle || '',
    hero_cta_text: form.hero_cta_text || '', hero_cta_secondary_text: form.hero_cta_secondary_text || '', hero_background_image: form.hero_background_image || null,
    stats_items: form.stats_items || DEFAULT_STATS_ITEMS,
    features_title: form.features_title || '', features_subtitle: form.features_subtitle || '', features: form.features || [],
    how_it_works_title: form.how_it_works_title || '', how_it_works_subtitle: form.how_it_works_subtitle || '', how_it_works_steps: form.how_it_works_steps || [],
    testimonials_title: form.testimonials_title || '', testimonials_subtitle: form.testimonials_subtitle || '',
    coaches_banner_title: form.coaches_banner_title || '', coaches_banner_subtitle: form.coaches_banner_subtitle || '',
    partners_title: form.partners_title || '', partners_subtitle: form.partners_subtitle || '',
    pricing_title: form.pricing_title || '', pricing_subtitle: form.pricing_subtitle || '',
    cta_title: form.cta_title || '', cta_subtitle: form.cta_subtitle || '', cta_button_text: form.cta_button_text || '', cta_secondary_text: form.cta_secondary_text || '',
    footer_description: form.footer_description || '',
    page_title: form.page_title || '', meta_description: form.meta_description || '', keywords: form.keywords || '', seo_og_image: form.seo_og_image || null,
  });

  const saveDraft = async (e) => {
    e?.preventDefault(); setErr(null); setSaving(true);
    try {
      await axios.put(`${API}/saas/landing-page/draft`, buildPayload(), sauth());
      notify('تم حفظ المسودة');
      await load();
    } catch (e2) { setErr(errMsg(e2, 'فشل حفظ المسودة')); }
    setSaving(false);
  };

  const publish = async () => {
    setErr(null); setPublishing(true);
    try {
      await saveDraft();
      await axios.post(`${API}/saas/landing-page/publish`, {}, sauth());
      notify('تم نشر التغييرات — تظهر الآن للزوار');
      await load();
    } catch (e2) { setErr(errMsg(e2, 'فشل النشر')); }
    setPublishing(false);
  };

  const discardDraft = async () => {
    setErr(null);
    try {
      await axios.post(`${API}/saas/landing-page/discard-draft`, {}, sauth());
      notify('تم تجاهل التعديلات غير المنشورة');
      await load();
    } catch (e2) { setErr(errMsg(e2, 'فشل التجاهل')); }
  };

  const revert = async () => {
    setErr(null);
    try {
      await axios.post(`${API}/saas/landing-page/revert`, {}, sauth());
      notify('تم الرجوع إلى النسخة المنشورة السابقة');
      await load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الرجوع للنسخة السابقة')); }
  };

  if (!form) return <div className="h-48 animate-pulse rounded-xl bg-slate-100" />;

  return (
    <div className="space-y-4" data-testid="superadmin-landing">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-slate-900">التحكم في الواجهة الرئيسية</h2>
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${meta.has_unpublished_changes ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-teal-700'}`} data-testid="landing-draft-status-badge">
            {meta.has_unpublished_changes ? 'مسودة بها تغييرات غير منشورة' : 'مطابقة للنسخة المنشورة'}
          </span>
        </div>
        <div className="text-xs text-slate-400" data-testid="landing-published-meta">
          {meta.published_at ? `آخر نشر: ${fmtDate(meta.published_at)} بواسطة ${meta.published_by || '—'}` : 'لم يُنشر المحتوى بعد'}
        </div>
      </div>

      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="landing-form-error">{err}</div>}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={saveDraft} disabled={saving} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300" data-testid="landing-save-draft-btn">
          <Save className="h-4 w-4" /> {saving ? 'جارٍ الحفظ...' : 'حفظ كمسودة'}
        </button>
        <button type="button" onClick={publish} disabled={publishing} className="flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700" data-testid="landing-publish-btn">
          <Send className="h-4 w-4" /> {publishing ? 'جارٍ النشر...' : 'نشر التعديلات'}
        </button>
        <button type="button" onClick={discardDraft} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600" data-testid="landing-discard-draft-btn">
          <XCircle className="h-4 w-4" /> إلغاء التعديلات غير المحفوظة
        </button>
        <button type="button" onClick={revert} disabled={!meta.can_revert} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:border-teal-300 disabled:cursor-not-allowed disabled:opacity-40" data-testid="landing-revert-btn">
          <RotateCcw className="h-4 w-4" /> الرجوع لآخر نسخة منشورة
        </button>
        <a href="/" target="_blank" rel="noreferrer" className="ms-auto rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300" data-testid="landing-preview-link">فتح الصفحة المنشورة</a>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          <SectionsManager sections={form.sections} setSections={(s) => setForm({ ...form, sections: s })} />

          <Card title="القسم الرئيسي (Hero)">
            <Field label="شارة صغيرة أعلى العنوان"><input className={inputCls} value={form.hero_badge_text || ''} onChange={(e) => setForm({ ...form, hero_badge_text: e.target.value })} data-testid="landing-hero-badge-input" /></Field>
            <Field label="العنوان الرئيسي"><input className={inputCls} value={form.hero_title || ''} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} data-testid="landing-hero-title" /></Field>
            <Field label="النص التعريفي"><textarea rows={2} className={inputCls} value={form.hero_subtitle || ''} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} data-testid="landing-hero-subtitle" /></Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="نص زر البداية"><input className={inputCls} value={form.hero_cta_text || ''} onChange={(e) => setForm({ ...form, hero_cta_text: e.target.value })} data-testid="landing-hero-cta-input" /></Field>
              <Field label="نص الزر الثانوي"><input className={inputCls} value={form.hero_cta_secondary_text || ''} onChange={(e) => setForm({ ...form, hero_cta_secondary_text: e.target.value })} data-testid="landing-hero-secondary-cta-input" /></Field>
            </div>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3">
              {form.hero_background_image && <img src={`${API}/files/${form.hero_background_image}`} alt="" className="h-14 w-24 rounded-lg object-cover" />}
              <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
                <ImagePlus className="h-3.5 w-3.5" /> {uploading ? 'جارٍ الرفع...' : 'رفع صورة خلفية (اختياري فوق الشبكة)'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadHeroBg} data-testid="landing-hero-bg-upload" />
              </label>
              {form.hero_background_image && <button type="button" onClick={() => setForm({ ...form, hero_background_image: null })} className="flex items-center gap-1 text-xs font-semibold text-red-600" data-testid="landing-hero-bg-remove"><XIcon className="h-3.5 w-3.5" /> إزالة</button>}
            </div>
          </Card>

          <StatsItemsEditor form={form} setForm={setForm} liveStats={liveStats} />
          <FeaturesEditor form={form} setForm={setForm} />
          <HowItWorksEditor form={form} setForm={setForm} />

          <Card title="قسم آراء العملاء" note="آراء العملاء نفسها (الاسم والصورة والنص والتقييم) تُدار من تبويب «آراء العملاء» — هنا فقط عنوان القسم. يُخفى القسم تلقائياً من الصفحة الرئيسية إذا لم توجد آراء ظاهرة.">
            <Field label="العنوان"><input className={inputCls} value={form.testimonials_title || ''} onChange={(e) => setForm({ ...form, testimonials_title: e.target.value })} data-testid="landing-testimonials-title-input" /></Field>
            <Field label="النص الفرعي"><input className={inputCls} value={form.testimonials_subtitle || ''} onChange={(e) => setForm({ ...form, testimonials_subtitle: e.target.value })} data-testid="landing-testimonials-subtitle-input" /></Field>
          </Card>

          <Card title="شريط المدربين" note="شريط دعوة يوجّه المدربين إلى صفحة تسيار للمدربين (/coaches).">
            <Field label="العنوان"><input className={inputCls} value={form.coaches_banner_title || ''} onChange={(e) => setForm({ ...form, coaches_banner_title: e.target.value })} data-testid="landing-coaches-banner-title-input" /></Field>
            <Field label="النص الفرعي"><input className={inputCls} value={form.coaches_banner_subtitle || ''} onChange={(e) => setForm({ ...form, coaches_banner_subtitle: e.target.value })} data-testid="landing-coaches-banner-subtitle-input" /></Field>
          </Card>

          <Card title="قسم الشركاء (مخفي حالياً)" note="شعارات الشركاء تُدار من تبويب «الشركاء». القسم مخفي افتراضياً — أظهره من إدارة الأقسام أعلاه بعد إضافة شركاء فعليين.">
            <Field label="العنوان الفرعي"><input className={inputCls} value={form.partners_title || ''} onChange={(e) => setForm({ ...form, partners_title: e.target.value })} data-testid="landing-partners-title-input" /></Field>
            <Field label="العنوان الرئيسي"><input className={inputCls} value={form.partners_subtitle || ''} onChange={(e) => setForm({ ...form, partners_subtitle: e.target.value })} data-testid="landing-partners-subtitle-input" /></Field>
          </Card>

          <Card title="قسم الباقات (مخفي حالياً)" note="الباقات نفسها تُدار من تبويب «باقات الأسعار». القسم مخفي افتراضياً — أظهره من إدارة الأقسام أعلاه بعد اعتماد الأسعار.">
            <Field label="العنوان"><input className={inputCls} value={form.pricing_title || ''} onChange={(e) => setForm({ ...form, pricing_title: e.target.value })} data-testid="landing-pricing-title-input" /></Field>
            <Field label="النص الفرعي"><input className={inputCls} value={form.pricing_subtitle || ''} onChange={(e) => setForm({ ...form, pricing_subtitle: e.target.value })} data-testid="landing-pricing-subtitle-input" /></Field>
          </Card>

          <Card title="قسم دعوة للعمل (CTA)">
            <Field label="العنوان"><input className={inputCls} value={form.cta_title || ''} onChange={(e) => setForm({ ...form, cta_title: e.target.value })} data-testid="landing-cta-title-input" /></Field>
            <Field label="النص الفرعي"><textarea rows={2} className={inputCls} value={form.cta_subtitle || ''} onChange={(e) => setForm({ ...form, cta_subtitle: e.target.value })} data-testid="landing-cta-subtitle-input" /></Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="نص الزر الأساسي"><input className={inputCls} value={form.cta_button_text || ''} onChange={(e) => setForm({ ...form, cta_button_text: e.target.value })} data-testid="landing-cta-button-input" /></Field>
              <Field label="نص الزر الثانوي"><input className={inputCls} value={form.cta_secondary_text || ''} onChange={(e) => setForm({ ...form, cta_secondary_text: e.target.value })} data-testid="landing-cta-secondary-input" /></Field>
            </div>
          </Card>

          <Card title="التذييل" note="بيانات التواصل وروابط التواصل الاجتماعي تُدار من تبويب «إعدادات المنصة» فقط لتظهر بشكل موحّد في كل الموقع.">
            <Field label="وصف مختصر عن المنصة (يظهر في التذييل)"><textarea rows={2} className={inputCls} value={form.footer_description || ''} onChange={(e) => setForm({ ...form, footer_description: e.target.value })} data-testid="landing-footer-description-input" /></Field>
          </Card>

          <Card title="إعدادات SEO">
            <Field label="عنوان تبويب المتصفح"><input className={inputCls} value={form.page_title || ''} onChange={(e) => setForm({ ...form, page_title: e.target.value })} data-testid="landing-seo-title-input" /></Field>
            <Field label="وصف الصفحة (Meta Description)"><textarea rows={2} className={inputCls} value={form.meta_description || ''} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} data-testid="landing-seo-description-input" /></Field>
            <Field label="الكلمات الأساسية (مفصولة بفواصل)"><input className={inputCls} value={form.keywords || ''} onChange={(e) => setForm({ ...form, keywords: e.target.value })} data-testid="landing-seo-keywords-input" /></Field>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3">
              {form.seo_og_image && <img src={`${API}/files/${form.seo_og_image}`} alt="" className="h-14 w-24 rounded-lg object-cover" />}
              <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
                <ImagePlus className="h-3.5 w-3.5" /> {uploadingOg ? 'جارٍ الرفع...' : 'رفع صورة المشاركة (og:image)'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadOgImage} data-testid="landing-seo-og-upload" />
              </label>
              {form.seo_og_image && <button type="button" onClick={() => setForm({ ...form, seo_og_image: null })} className="flex items-center gap-1 text-xs font-semibold text-red-600" data-testid="landing-seo-og-remove"><XIcon className="h-3.5 w-3.5" /> إزالة</button>}
            </div>
          </Card>
        </div>

        <LandingPreviewPane form={form} />
      </div>
    </div>
  );
}

// ====================== آراء العملاء (Testimonials) ======================
export function TestimonialsTab({ notify }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/testimonials`, sauth()); setItems(Array.isArray(res.data) ? res.data : []); } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (t) => {
    setEditing(t || null);
    setForm(t ? { ...t } : { customer_name: '', job_title: '', org_name: '', photo: '', quote: '', rating: 5, is_active: true, order_index: items.length });
    setErr(null); setModal(true);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      setForm((f) => ({ ...f, photo: res.data?.file_id }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الصورة')); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.customer_name?.trim()) { setErr('اسم العميل مطلوب'); return; }
    if (!form.quote?.trim()) { setErr('نص الرأي مطلوب'); return; }
    const payload = { customer_name: form.customer_name.trim(), job_title: form.job_title || '', org_name: form.org_name || '', photo: form.photo || null, quote: form.quote.trim(), rating: Number(form.rating) || 5, is_active: form.is_active !== false, order_index: Number(form.order_index) || 0 };
    try {
      if (editing) await axios.put(`${API}/testimonials/${editing.id}`, payload, sauth());
      else await axios.post(`${API}/testimonials`, payload, sauth());
      setModal(false); notify('تم حفظ الرأي'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/testimonials/${confirmDel.id}`, sauth()); notify('تم حذف الرأي'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-testimonials">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">آراء العملاء ({items.length})</h2>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="testimonial-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة رأي</PrimaryBtn>
      </div>
      {loading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-100" />)}</div>
        : items.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="testimonials-empty">لا توجد آراء بعد — قسم آراء العملاء يبقى مخفياً في الصفحة الرئيسية حتى تُضيف رأياً واحداً على الأقل</div>
        : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="testimonial-card">
                <div className="flex items-center gap-3">
                  {t.photo ? <img src={`${API}/files/${t.photo}`} alt="" className="h-11 w-11 rounded-full bg-slate-50 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    : <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#16233b]/10 text-sm font-bold text-[#16233b]">{(t.customer_name || '؟').slice(0, 2)}</div>}
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-slate-900">{t.customer_name}</div>
                    <div className="truncate text-xs text-slate-500">{[t.job_title, t.org_name].filter(Boolean).join(' — ')}</div>
                  </div>
                </div>
                <p className="mt-2 line-clamp-3 text-xs text-slate-500">{t.quote}</p>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex">{Array.from({ length: 5 }).map((_, i) => <StarIcon key={i} className={`h-3 w-3 ${i < (t.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />)}</div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${t.is_active !== false ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{t.is_active !== false ? 'ظاهر' : 'مخفي'}</span>
                </div>
                <div className="mt-3 flex justify-end gap-1 border-t border-slate-50 pt-2">
                  <button onClick={() => openForm(t)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="testimonial-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                  <button onClick={() => setConfirmDel(t)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="testimonial-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Modal open={modal} title={editing ? 'تعديل الرأي' : 'إضافة رأي'} onClose={() => setModal(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="testimonial-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="اسم العميل" required><input className={inputCls} value={form.customer_name || ''} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="testimonial-input-name" /></Field>
            <Field label="المسمى الوظيفي"><input className={inputCls} value={form.job_title || ''} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="testimonial-input-job" /></Field>
          </div>
          <Field label="اسم المنشأة"><input className={inputCls} value={form.org_name || ''} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="testimonial-input-org" /></Field>
          <Field label="نص الرأي" required><textarea rows={3} className={inputCls} value={form.quote || ''} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="testimonial-input-quote" /></Field>
          <div className="flex items-center gap-3">
            {form.photo && <img src={`${API}/files/${form.photo}`} alt="" className="h-11 w-11 rounded-full object-cover" />}
            <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
              <ImagePlus className="h-3.5 w-3.5" /> {uploading ? 'جارٍ الرفع...' : 'صورة اختيارية'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="testimonial-photo-upload" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="التقييم">
              <select className={inputCls} value={form.rating ?? 5} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="testimonial-input-rating">
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} نجوم</option>)}
              </select>
            </Field>
            <Field label="الترتيب"><input type="number" min="0" className={inputCls} value={form.order_index ?? 0} onChange={(e) => setForm({ ...form, order_index: e.target.value })} data-testid="testimonial-input-order" /></Field>
            <label className="mt-6 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="testimonial-input-active" /> ظاهر</label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="testimonial-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف الرأي" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف رأي «{confirmDel?.customer_name}»؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="testimonial-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

export function UsersTab({ notify }) {
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [clubFilter, setClubFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const url = clubFilter ? `${API}/users?club_id=${clubFilter}` : `${API}/users`;
    try { const res = await axios.get(url, sauth()); setUsers(Array.isArray(res.data) ? res.data : []); } catch { setUsers([]); }
    setLoading(false);
  }, [clubFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/saas/admin/clubs?limit=100`, sauth()).then((r) => setClubs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const clubName = (id) => clubs.find((c) => c.id === id)?.name || (id ? '—' : 'المنصة');

  const toggleActive = async (u) => {
    try { await axios.put(`${API}/users/${u.id}`, { is_active: !(u.is_active !== false) }, sauth()); notify('تم تحديث حالة المستخدم'); load(); }
    catch (e2) { notify(errMsg(e2, 'فشل التحديث')); }
  };

  const submitEdit = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      const payload = { full_name: form.full_name, email: form.email, role: form.role };
      if (form.password) payload.password = form.password;
      await axios.put(`${API}/users/${editing.id}`, payload, sauth());
      setEditing(null); notify('تم حفظ المستخدم'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const submitCreate = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.username?.trim() || !form.full_name?.trim()) { setErr('اسم الدخول والاسم الكامل مطلوبان'); return; }
    if (!form.club_id) { setErr('اختر المنشأة التابع لها الحساب'); return; }
    if (!form.password || form.password.length < 6) { setErr('كلمة المرور يجب ألا تقل عن 6 أحرف'); return; }
    try {
      await axios.post(`${API}/users`, {
        username: form.username.trim(), password: form.password, full_name: form.full_name.trim(),
        email: form.email || '', role: form.role || 'manager', is_active: true, club_id: form.club_id,
      }, sauth());
      setCreating(false); notify('تم إنشاء الحساب بنجاح'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل إنشاء الحساب')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/users/${confirmDel.id}`, sauth()); notify('تم حذف المستخدم'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const filtered = users.filter((u) => {
    const q = search.trim();
    return !q || u.full_name?.includes(q) || u.username?.includes(q);
  });

  return (
    <div className="space-y-4" data-testid="superadmin-users">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-slate-900">مستخدمو المنصة ({users.length})</h2>
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700" data-testid="sa-users-online-badge">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {users.filter((u) => u.is_online).length} متصل الآن
          </span>
        </div>
        <PrimaryBtn onClick={() => { setCreating(true); setForm({ username: '', full_name: '', email: '', password: '', role: 'manager', club_id: clubFilter || '' }); setErr(null); }} data-testid="sa-user-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة حساب</PrimaryBtn>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم..." className="w-40 bg-transparent text-sm outline-none" data-testid="sa-user-search" />
          </div>
          <select value={clubFilter} onChange={(e) => setClubFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="sa-user-club-filter">
            <option value="">كل المنشآت</option>
            {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="mr-auto text-xs text-slate-400">{filtered.length} مستخدم</span>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-11 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2 font-semibold">المستخدم</th>
                  <th className="px-2 py-2 font-semibold">اسم الدخول</th>
                  <th className="px-2 py-2 font-semibold">المنشأة</th>
                  <th className="px-2 py-2 font-semibold">الدور</th>
                  <th className="px-2 py-2 font-semibold">آخر دخول / الاتصال</th>
                  <th className="px-2 py-2 font-semibold">الحالة</th>
                  <th className="px-2 py-2"></th>
                </tr></thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="sa-user-row">
                      <td className="px-4 py-2 font-semibold text-slate-800">{u.full_name || '—'}</td>
                      <td className="px-2 py-2 text-slate-600" dir="ltr">{u.username}</td>
                      <td className="px-2 py-2 text-slate-600">{u.club_name || clubName(u.club_id)}</td>
                      <td className="px-2 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{ROLE_LABELS[u.role] || u.role}</span></td>
                      <td className="px-2 py-2">
                        <ActivityCell testId="sa-user-activity-cell" online={u.is_online} lastLogin={u.last_login} lastSeen={u.last_seen} createdAt={u.created_at} />
                      </td>
                      <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${u.is_active !== false ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`}>{u.is_active !== false ? 'مفعل' : 'معطل'}</span></td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => { setEditing(u); setForm({ full_name: u.full_name || '', email: u.email || '', role: u.role, password: '' }); setErr(null); }} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="sa-user-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                          <button onClick={() => toggleActive(u)} title={u.is_active !== false ? 'تعطيل' : 'تفعيل'} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-amber-600" data-testid="sa-user-toggle-btn"><Icon name={u.is_active !== false ? 'x' : 'check'} className="w-4 h-4" /></button>
                          {!u.is_super_admin && <button onClick={() => setConfirmDel(u)} title="حذف" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="sa-user-del-btn"><Icon name="trash" className="w-4 h-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Modal open={creating} title="إنشاء حساب جديد" onClose={() => setCreating(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="sa-user-create-error">{err}</div>}
        <form onSubmit={submitCreate} className="space-y-3">
          <Field label="المنشأة" required>
            <select className={inputCls} value={form.club_id || ''} onChange={(e) => setForm({ ...form, club_id: e.target.value })} data-testid="sa-user-create-club">
              <option value="">اختر المنشأة</option>
              {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="الاسم الكامل" required><input className={inputCls} value={form.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="sa-user-create-fullname" /></Field>
          <Field label="اسم الدخول" required><input dir="ltr" className={inputCls} value={form.username || ''} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="sa-user-create-username" /></Field>
          <Field label="البريد الإلكتروني"><input dir="ltr" type="email" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="كلمة المرور" required><input dir="ltr" type="password" className={inputCls} value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sa-user-create-password" /></Field>
          <Field label="الدور">
            <select className={inputCls} value={form.role || 'manager'} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="sa-user-create-role">
              {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'super_admin').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="sa-user-create-submit">إنشاء الحساب</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(editing)} title={`تعديل: ${editing?.username || ''}`} onClose={() => setEditing(null)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="sa-user-form-error">{err}</div>}
        <form onSubmit={submitEdit} className="space-y-3">
          <Field label="الاسم الكامل"><input className={inputCls} value={form.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
          <Field label="البريد الإلكتروني"><input dir="ltr" type="email" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="الدور">
            <select className={inputCls} value={form.role || ''} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'super_admin').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="كلمة مرور جديدة (اختياري)"><input dir="ltr" type="password" className={inputCls} value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="sa-user-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف المستخدم" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف حساب «{confirmDel?.full_name || confirmDel?.username}» نهائياً؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="sa-user-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

// ====================== الاشتراكات ======================
export function SubscriptionsTab({ notify }) {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/saas/admin/clubs?limit=100`, sauth()); setClubs(Array.isArray(res.data) ? res.data : []); } catch { setClubs([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      await axios.put(`${API}/saas/admin/clubs/${editing.id}/subscription`, {
        plan: form.plan, status: form.status, end_date: form.end_date || null,
      }, sauth());
      setEditing(null); notify('تم تحديث الاشتراك'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل تحديث الاشتراك')); }
  };

  const expired = clubs.filter((c) => c.subscription_status === 'expired').length;
  const trial = clubs.filter((c) => c.subscription_status === 'trial').length;
  const active = clubs.filter((c) => c.subscription_status === 'active').length;

  return (
    <div className="space-y-4" data-testid="superadmin-subscriptions">
      <h2 className="text-lg font-bold text-slate-900">الاشتراكات والخطط</h2>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">نشطة</div><div className="text-xl font-bold text-teal-700">{active}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">تجريبية</div><div className="text-xl font-bold text-amber-600">{trial}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">منتهية</div><div className="text-xl font-bold text-red-600">{expired}</div></div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-11 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                <th className="px-4 py-2 font-semibold">المنشأة</th>
                <th className="px-2 py-2 font-semibold">الخطة</th>
                <th className="px-2 py-2 font-semibold">حالة الاشتراك</th>
                <th className="px-2 py-2 font-semibold">ينتهي في</th>
                <th className="px-2 py-2"></th>
              </tr></thead>
              <tbody>
                {clubs.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="subscription-row">
                    <td className="px-4 py-2 font-semibold text-slate-800">{c.name}</td>
                    <td className="px-2 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{PLAN_LABELS[c.subscription_plan] || c.subscription_plan || '—'}</span></td>
                    <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${SUB_STATUS[c.subscription_status]?.[1] || 'bg-slate-100 text-slate-500'}`}>{SUB_STATUS[c.subscription_status]?.[0] || c.subscription_status || '—'}</span></td>
                    <td className="px-2 py-2 text-xs text-slate-500">{c.subscription_end_date ? fmtDate(c.subscription_end_date) : '—'}</td>
                    <td className="px-2 py-2 text-left">
                      <button onClick={() => { setEditing(c); setForm({ plan: c.subscription_plan || 'free', status: c.subscription_status || 'trial', end_date: c.subscription_end_date ? String(c.subscription_end_date).slice(0, 10) : '' }); setErr(null); }} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-teal-300 hover:text-teal-700" data-testid="subscription-edit-btn">تعديل الاشتراك</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Modal open={Boolean(editing)} title={`اشتراك: ${editing?.name || ''}`} onClose={() => setEditing(null)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="subscription-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="الخطة">
            <select className={inputCls} value={form.plan || 'free'} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="subscription-input-plan">
              {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="الحالة">
            <select className={inputCls} value={form.status || 'trial'} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="subscription-input-status">
              {Object.entries(SUB_STATUS).map(([k, [v]]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="تاريخ نهاية الاشتراك"><input type="date" className={inputCls} value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="subscription-input-end" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="subscription-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ====================== باقات الاشتراك (تُعرض في الصفحة الرئيسية) ======================
const BADGE_PRESETS = ['الأكثر اختياراً', 'الأفضل قيمة', 'موصى بها', 'للمحترفين', 'بداية موفقة', 'توفير سنوي', 'مميزة'];

export function PlansTab({ notify }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/saas/admin/plans`, sauth()); setPlans(Array.isArray(res.data) ? res.data : []); } catch { setPlans([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (p) => {
    const badgeText = p.badge_text || '';
    const badgeMode = badgeText === '' ? 'none' : (BADGE_PRESETS.includes(badgeText) ? badgeText : 'custom');
    setEditing(p);
    setForm({
      ...p,
      features_text: (p.features || []).join('\n'),
      badge_mode: badgeMode,
      badge_custom: badgeMode === 'custom' ? badgeText : '',
      annual_discount_percent: p.annual_discount_percent ?? 15,
      annual_price_input: p.annual_price != null ? p.annual_price : '',
    });
    setErr(null);
  };

  const monthlyPrice = Number(form.price) || 0;
  const discountPct = Number(form.annual_discount_percent) || 0;
  const autoAnnual = monthlyPrice > 0 ? Math.round(monthlyPrice * 12 * (1 - discountPct / 100) * 100) / 100 : 0;

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    const badgeText = form.badge_mode === 'none' ? '' : (form.badge_mode === 'custom' ? (form.badge_custom || '').trim() : form.badge_mode);
    try {
      await axios.put(`${API}/saas/admin/plans/${editing.plan_id}`, {
        display_name: form.display_name, tagline: form.tagline || '',
        price: Number(form.price) || 0, currency: form.currency || 'SAR',
        billing_period: form.billing_period || 'شهرياً',
        features: (form.features_text || '').split('\n').map((f) => f.trim()).filter(Boolean),
        is_visible: form.is_visible !== false,
        cta_text: form.cta_text || 'ابدأ الآن',
        badge_text: badgeText,
        annual_discount_percent: discountPct,
        annual_price: form.annual_price_input === '' ? null : Number(form.annual_price_input),
      }, sauth());
      setEditing(null); notify('تم حفظ الباقة'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-plans">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">باقات الاشتراك (تظهر في الصفحة الرئيسية)</h2>
        <a href="/#pricing" target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300">معاينة قسم الأسعار</a>
      </div>
      {loading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 animate-pulse rounded-xl bg-slate-100" />)}</div>
        : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((p) => (
              <div key={p.plan_id} className={`rounded-xl border bg-white p-4 ${p.badge_text ? 'border-teal-400 ring-1 ring-teal-200' : 'border-slate-200'}`} data-testid="plan-card">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-900">{p.display_name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.is_visible ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_visible ? 'ظاهرة' : 'مخفية'}</span>
                </div>
                {p.badge_text && <span className="mt-1 inline-block rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700" data-testid="plan-badge-preview">{p.badge_text}</span>}
                <div className="mt-2 text-2xl font-bold text-slate-800">{p.price ? `${p.price} ${p.currency}` : 'مجاناً'}</div>
                <div className="text-xs text-slate-500">{p.billing_period}</div>
                {p.price > 0 && <div className="mt-1 text-xs text-teal-700">سنوياً: {p.annual_price_effective} {p.currency} {p.annual_savings_percent > 0 && `(وفر ${p.annual_savings_percent}%)`}</div>}
                <ul className="mt-3 space-y-1 text-xs text-slate-600">
                  {(p.features || []).slice(0, 3).map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
                <button onClick={() => openForm(p)} className="mt-3 w-full rounded-lg border border-slate-200 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal-300 hover:text-teal-700" data-testid="plan-edit-btn">تعديل الباقة</button>
              </div>
            ))}
          </div>
        )}

      <Modal open={Boolean(editing)} title={`تعديل باقة: ${editing?.display_name || ''}`} onClose={() => setEditing(null)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="plan-form-error">{err}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="اسم الباقة" required><input className={inputCls} value={form.display_name || ''} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="plan-input-name" /></Field>
          <Field label="الوصف المختصر"><input className={inputCls} value={form.tagline || ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></Field>
          <Field label="السعر الشهري (0 = مجاناً)"><input type="number" min="0" className={inputCls} value={form.price ?? 0} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="plan-input-price" /></Field>
          <Field label="فترة الفوترة"><input className={inputCls} value={form.billing_period || ''} onChange={(e) => setForm({ ...form, billing_period: e.target.value })} /></Field>
          {monthlyPrice > 0 && (
            <>
              <Field label="نسبة الخصم السنوي %"><input type="number" min="0" max="100" className={inputCls} value={form.annual_discount_percent ?? 15} onChange={(e) => setForm({ ...form, annual_discount_percent: e.target.value })} data-testid="plan-input-annual-discount" /></Field>
              <Field label={`السعر السنوي (تلقائي: ${autoAnnual})`}>
                <input type="number" min="0" placeholder={`${autoAnnual}`} className={inputCls} value={form.annual_price_input ?? ''} onChange={(e) => setForm({ ...form, annual_price_input: e.target.value })} data-testid="plan-input-annual-price" />
              </Field>
            </>
          )}
          <Field label="نص زر الاشتراك"><input className={inputCls} value={form.cta_text || ''} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} /></Field>
          <Field label="شارة الباقة">
            <select className={inputCls} value={form.badge_mode || 'none'} onChange={(e) => setForm({ ...form, badge_mode: e.target.value })} data-testid="plan-input-badge-mode">
              <option value="none">بدون شارة</option>
              {BADGE_PRESETS.map((b) => <option key={b} value={b}>{b}</option>)}
              <option value="custom">أخرى (نص مخصص)</option>
            </select>
          </Field>
          {form.badge_mode && !['none', ...BADGE_PRESETS].includes(form.badge_mode) && (
            <Field label="نص الشارة المخصص"><input className={inputCls} value={form.badge_custom || ''} onChange={(e) => setForm({ ...form, badge_custom: e.target.value })} data-testid="plan-input-badge-custom" /></Field>
          )}
          <div className="flex items-center gap-4 pt-6">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_visible !== false} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} /> ظاهرة في الصفحة الرئيسية</label>
          </div>
          <div className="col-span-full"><Field label="المزايا (كل ميزة في سطر)"><textarea rows={5} className={inputCls} value={form.features_text || ''} onChange={(e) => setForm({ ...form, features_text: e.target.value })} data-testid="plan-input-features" /></Field></div>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="plan-form-submit">حفظ الباقة</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ====================== منظومة المدربين ======================
const COACH_TYPE_LABELS = { head_coach: 'مدرب رئيسي', assistant_coach: 'مدرب مساعد', goalkeeper_coach: 'مدرب حراس مرمى', fitness_coach: 'مدرب لياقة', conditioning_coach: 'مدرب إعداد بدني', performance_analyst: 'محلل أداء', youth_coach: 'مدرب فئات سنية', other: 'تخصص آخر' };
const VISIBILITY_LABELS = { private: 'خاص', organizations_only: 'المنشآت فقط', public_directory: 'الدليل العام' };

export function CoachesTab({ notify }) {
  const [stats, setStats] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [pendingCerts, setPendingCerts] = useState([]);
  const [subTab, setSubTab] = useState('coaches');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [subModal, setSubModal] = useState(null);
  const [subForm, setSubForm] = useState({});
  const [subErr, setSubErr] = useState(null);
  const [waTarget, setWaTarget] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        axios.get(`${API}/coaches/admin/stats`, sauth()),
        axios.get(`${API}/coaches/admin/list?page_size=100`, sauth()),
        axios.get(`${API}/coaches/admin/certifications/pending`, sauth()),
      ]);
      setStats(s.data); setCoaches(c.data.items); setPendingCerts(p.data);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (coachId, isActive) => {
    try {
      await axios.put(`${API}/coaches/admin/${coachId}/status`, null, { ...sauth(), params: { is_active: !isActive } });
      notify(!isActive ? 'تم إعادة تفعيل الحساب' : 'تم تعليق الحساب');
      load();
    } catch (e) { notify(errMsg(e, 'فشل التحديث')); }
  };

  const verifyCert = async (certId, status) => {
    try {
      await axios.put(`${API}/coaches/admin/certifications/${certId}/verify`, null, { ...sauth(), params: { verify_status: status } });
      notify(status === 'verified' ? 'تم توثيق الشهادة' : 'تم رفض الشهادة');
      load();
    } catch (e) { notify(errMsg(e, 'فشل التحديث')); }
  };

  const openSubModal = (c) => {
    setSubModal(c);
    setSubForm({ plan_type: c.subscription?.plan_type || 'trial', status: c.subscription?.status || 'trial', end_date: c.subscription?.end_date ? String(c.subscription.end_date).slice(0, 10) : '', amount: c.subscription?.amount ?? '' });
    setSubErr(null);
  };

  const submitSub = async (e) => {
    e.preventDefault(); setSubErr(null);
    try {
      await axios.put(`${API}/coaches/admin/${subModal.id}/subscription`, {
        plan_type: subForm.plan_type, status: subForm.status,
        end_date: subForm.end_date ? `${subForm.end_date}T00:00:00` : null, amount: subForm.amount === '' ? null : Number(subForm.amount),
      }, sauth());
      setSubModal(null); notify('تم تحديث اشتراك المدرب بنجاح'); load();
    } catch (e2) { setSubErr(errMsg(e2, 'فشل تحديث الاشتراك')); }
  };

  const endSubscription = async () => {
    try {
      await axios.put(`${API}/coaches/admin/${confirmEnd.id}/subscription`, { status: 'expired' }, sauth());
      notify('تم إنهاء اشتراك المدرب'); setConfirmEnd(null); load();
    } catch (e2) { notify(errMsg(e2, 'فشلت العملية')); setConfirmEnd(null); }
  };

  const STAT_CARDS = stats ? [
    ['إجمالي المدربين', stats.total_coaches], ['ظاهرون بالدليل العام', stats.visible_public],
    ['متاحون حالياً', stats.available_now], ['شهادات قيد المراجعة', stats.pending_certifications],
    ['طلبات قائمة', stats.active_requests], ['تعاقدات ناجحة', stats.hired_total],
  ] : [];

  const filteredCoaches = coaches.filter((c) => {
    if (statusFilter === 'active' && !c.is_active) return false;
    if (statusFilter === 'suspended' && c.is_active) return false;
    if (expiringOnly) {
      const dl = daysRemaining(c.subscription?.end_date);
      if (dl === null || dl > 14) return false;
    }
    const q = search.trim();
    return !q || c.full_name_ar?.includes(q);
  });

  const SUB_STATUS_LABELS = { trial: 'تجريبي', active: 'نشط', expired: 'منتهي', none: 'بلا اشتراك' };
  const SUB_STATUS_CLS = { trial: 'bg-amber-50 text-amber-700', active: 'bg-teal-50 text-teal-700', expired: 'bg-red-50 text-red-600', none: 'bg-slate-100 text-slate-500' };

  return (
    <div className="space-y-4" data-testid="superadmin-coaches">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {STAT_CARDS.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-3" data-testid="coach-admin-stat-card">
            <div className="text-2xl font-bold text-slate-800">{value}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit">
        <button onClick={() => setSubTab('coaches')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${subTab === 'coaches' ? 'bg-teal-600 text-white' : 'text-slate-600'}`} data-testid="coach-admin-subtab-coaches">قائمة المدربين</button>
        <button onClick={() => setSubTab('certs')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${subTab === 'certs' ? 'bg-teal-600 text-white' : 'text-slate-600'}`} data-testid="coach-admin-subtab-certs">مراجعة الشهادات ({pendingCerts.length})</button>
        <button onClick={() => setSubTab('plan')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${subTab === 'plan' ? 'bg-teal-600 text-white' : 'text-slate-600'}`} data-testid="coach-admin-subtab-plan">باقة المدرب</button>
      </div>

      {subTab === 'plan' ? <CoachPlanTab notify={notify} /> : loading ? <p className="text-sm text-slate-400">جارِ التحميل...</p> : subTab === 'coaches' ? (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
              <Icon name="search" className="w-4 h-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم..." className="w-48 bg-transparent text-sm outline-none" data-testid="coach-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="coach-status-filter">
              <option value="">كل الحالات</option>
              <option value="active">نشط</option>
              <option value="suspended">معطل</option>
            </select>
            <label className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600">
              <input type="checkbox" checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} data-testid="coach-expiring-filter" />
              قرب الانتهاء (≤14 يوم)
            </label>
            <span className="mr-auto text-xs text-slate-400">{filteredCoaches.length} مدرب</span>
          </div>

          {filteredCoaches.length === 0 ? <p className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">لا يوجد مدربون مطابقون</p> : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500"><tr>
                  <th className="p-3 text-right">الاسم</th><th className="p-3 text-right">التواصل</th>
                  <th className="p-3 text-right">الاشتراك</th><th className="p-3 text-right">الأيام المتبقية</th>
                  <th className="p-3 text-right">الحالة</th><th className="p-3 text-right">إجراء</th>
                </tr></thead>
                <tbody>
                  {filteredCoaches.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid="coach-admin-row">
                      <td className="p-3 font-semibold text-slate-800">{c.full_name_ar}</td>
                      <td className="p-3">
                        {c.phone ? (
                          <a href={buildWaMeLink(normalizeWhatsAppNumber(c.phone), '')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal-600" dir="ltr" data-testid="coach-phone-link">
                            <Icon name="whatsapp" className="w-3.5 h-3.5" /> {c.phone}
                          </a>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${SUB_STATUS_CLS[c.subscription?.status] || SUB_STATUS_CLS.none}`}>{SUB_STATUS_LABELS[c.subscription?.status] || 'بلا اشتراك'}</span></td>
                      <td className="p-3"><DaysBar endDate={c.subscription?.end_date} testId="coach-days-bar" /></td>
                      <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.is_active ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`}>{c.is_active ? 'نشط' : 'معطل'}</span></td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setWaTarget(c)} title="تذكير دفع واتساب" className="rounded-lg p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600" data-testid="coach-whatsapp-btn"><Icon name="whatsapp" className="w-4 h-4" /></button>
                          <button onClick={() => openSubModal(c)} title="تجديد / تعديل الاشتراك" className="rounded-lg p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600" data-testid="coach-renew-btn"><Icon name="refresh" className="w-4 h-4" /></button>
                          <button onClick={() => toggleStatus(c.id, c.is_active)} title={c.is_active ? 'تعليق' : 'تفعيل'} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="coach-admin-toggle-status-btn">
                            <Icon name={c.is_active ? 'x' : 'check'} className="w-4 h-4" />
                          </button>
                          <button onClick={() => setConfirmEnd(c)} title="إنهاء الاشتراك" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="coach-end-sub-btn"><Icon name="lock" className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        pendingCerts.length === 0 ? <p className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">لا توجد شهادات بحاجة مراجعة</p> : (
          <div className="space-y-2">
            {pendingCerts.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4" data-testid="coach-admin-cert-row">
                <div>
                  <div className="text-sm font-bold text-slate-800">{c.name} — {c.coach_name}</div>
                  <div className="text-xs text-slate-500">{c.issuing_authority} {c.level && `· ${c.level}`}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => verifyCert(c.id, 'verified')} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="coach-admin-cert-verify-btn">توثيق</button>
                  <button onClick={() => verifyCert(c.id, 'rejected')} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600" data-testid="coach-admin-cert-reject-btn">رفض</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Modal open={Boolean(subModal)} title={`تجديد / تعديل اشتراك: ${subModal?.full_name_ar || ''}`} onClose={() => setSubModal(null)}>
        {subErr && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="coach-sub-error">{subErr}</div>}
        <form onSubmit={submitSub} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="نوع الباقة">
            <select className={inputCls} value={subForm.plan_type} onChange={(e) => setSubForm({ ...subForm, plan_type: e.target.value })} data-testid="coach-sub-plan-type">
              <option value="trial">تجريبي</option>
              <option value="monthly">شهري</option>
              <option value="yearly">سنوي</option>
            </select>
          </Field>
          <Field label="الحالة">
            <select className={inputCls} value={subForm.status} onChange={(e) => setSubForm({ ...subForm, status: e.target.value })} data-testid="coach-sub-status">
              <option value="trial">تجريبي</option>
              <option value="active">نشط</option>
              <option value="expired">منتهي</option>
              <option value="none">بلا اشتراك</option>
            </select>
          </Field>
          <Field label="تاريخ نهاية الاشتراك"><input type="date" className={inputCls} value={subForm.end_date} onChange={(e) => setSubForm({ ...subForm, end_date: e.target.value })} data-testid="coach-sub-end-date" /></Field>
          <Field label="المبلغ المستحق (ريال)"><input type="number" min="0" step="0.01" className={inputCls} value={subForm.amount} onChange={(e) => setSubForm({ ...subForm, amount: e.target.value })} data-testid="coach-sub-amount" /></Field>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setSubModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="coach-sub-submit">حفظ الاشتراك</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmEnd)} title="إنهاء الاشتراك" onClose={() => setConfirmEnd(null)}>
        <p className="text-sm text-slate-600">سيتم تحويل حالة اشتراك «{confirmEnd?.full_name_ar}» إلى منتهي فوراً. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmEnd(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={endSubscription} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="coach-confirm-end-sub">إنهاء الاشتراك</button>
        </div>
      </Modal>

      <AdminWhatsAppModal
        open={Boolean(waTarget)}
        onClose={() => setWaTarget(null)}
        type="coach_subscription"
        entity={waTarget ? { name: waTarget.full_name_ar, phone: waTarget.phone, plan_name: waTarget.subscription?.plan_type, expiry_date: waTarget.subscription?.end_date, amount: waTarget.subscription?.amount } : null}
      />
    </div>
  );
}

// ====================== باقة المدرب المحترف ======================
function CoachPlanTab({ notify }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/coaches/plan`, sauth()); setForm(res.data); } catch { setForm({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      const res = await axios.put(`${API}/coaches/plan`, {
        name: form.name, tagline: form.tagline,
        monthly_price: Number(form.monthly_price) || 0,
        yearly_price: Number(form.yearly_price) || 0,
        is_active: Boolean(form.is_active),
        features: form.features,
        discount_active: Boolean(form.discount_active),
        discount_percent: Number(form.discount_percent) || 0,
        discount_label: form.discount_label,
        free_trial_days: Number(form.free_trial_days) || 0,
      }, sauth());
      setForm(res.data);
      notify('تم حفظ باقة المدرب');
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
    setSaving(false);
  };

  const updateFeature = (i, val) => {
    const next = [...(form.features || [])];
    next[i] = val;
    setForm({ ...form, features: next });
  };
  const removeFeature = (i) => setForm({ ...form, features: (form.features || []).filter((_, idx) => idx !== i) });
  const addFeature = () => setForm({ ...form, features: [...(form.features || []), ''] });

  if (!form) return <p className="text-sm text-slate-400">جارِ التحميل...</p>;
  return (
    <form onSubmit={save} className="max-w-2xl space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="coach-plan-form">
      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <input type="checkbox" checked={Boolean(form.is_active)} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="coach-plan-active-toggle" className="h-4 w-4 rounded border-slate-300 text-teal-600" />
        عرض الباقة في صفحة تسيار للمدربين
      </label>
      <Field label="اسم الباقة"><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coach-plan-name-input" /></Field>
      <Field label="الوصف المختصر"><input className={inputCls} value={form.tagline || ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="السعر الشهري (ريال)"><input type="number" className={inputCls} value={form.monthly_price ?? ''} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} data-testid="coach-plan-monthly-input" /></Field>
        <Field label="السعر السنوي (ريال)"><input type="number" className={inputCls} value={form.yearly_price ?? ''} onChange={(e) => setForm({ ...form, yearly_price: e.target.value })} data-testid="coach-plan-yearly-input" /></Field>
      </div>

      <div className="rounded-lg border border-dashed border-teal-200 bg-teal-50/50 p-3 space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={Boolean(form.discount_active)} onChange={(e) => setForm({ ...form, discount_active: e.target.checked })} data-testid="coach-plan-discount-active-toggle" className="h-4 w-4 rounded border-slate-300 text-teal-600" />
          تفعيل خصم على الباقة (يُعرض السعر الأصلي مشطوباً بجانب السعر بعد الخصم)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="نسبة الخصم (%)"><input type="number" min="0" max="90" className={inputCls} value={form.discount_percent ?? ''} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="coach-plan-discount-percent-input" disabled={!form.discount_active} /></Field>
          <Field label="عنوان العرض"><input className={inputCls} value={form.discount_label || ''} onChange={(e) => setForm({ ...form, discount_label: e.target.value })} data-testid="coach-plan-discount-label-input" disabled={!form.discount_active} /></Field>
        </div>
      </div>

      <Field label="عدد أيام التجربة المجانية عند التسجيل">
        <input type="number" min="0" max="90" className={inputCls} value={form.free_trial_days ?? ''} onChange={(e) => setForm({ ...form, free_trial_days: e.target.value })} data-testid="coach-plan-trial-days-input" />
      </Field>
      <div>
        <div className="mb-1.5 text-xs font-bold text-slate-600">المزايا</div>
        <div className="space-y-2">
          {(form.features || []).map((f, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputCls} value={f} onChange={(e) => updateFeature(i, e.target.value)} data-testid={`coach-plan-feature-${i}`} />
              <button type="button" onClick={() => removeFeature(i)} className="rounded-lg border border-red-200 px-3 text-xs font-semibold text-red-600" data-testid={`coach-plan-feature-remove-${i}`}>حذف</button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addFeature} className="mt-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal-300" data-testid="coach-plan-feature-add-btn">+ إضافة ميزة</button>
      </div>
      <div className="flex justify-end"><PrimaryBtn type="submit" disabled={saving} data-testid="coach-plan-save-btn">{saving ? 'جارٍ الحفظ...' : 'حفظ الباقة'}</PrimaryBtn></div>
    </form>
  );
}

// ====================== الشركاء ======================
export function PartnersTab({ notify }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/partners`, sauth()); setPartners(Array.isArray(res.data) ? res.data : []); } catch { setPartners([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const logoUrl = (logo) => (!logo ? null : (logo.startsWith('http') ? logo : `${API}/files/${logo}`));

  const openForm = (p) => {
    setEditing(p || null);
    setForm(p ? { ...p } : { name: '', description: '', website: '', logo: '', is_active: true, order_index: partners.length });
    setErr(null); setModal(true);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setErr(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      setForm((f) => ({ ...f, logo: res.data?.file_id }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الشعار')); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.name?.trim()) { setErr('اسم الشريك مطلوب'); return; }
    const payload = { name: form.name.trim(), description: form.description || '', website: form.website || '', logo: form.logo || null, is_active: form.is_active !== false, order_index: Number(form.order_index) || 0 };
    try {
      if (editing) await axios.put(`${API}/partners/${editing.id}`, payload, sauth());
      else await axios.post(`${API}/partners`, payload, sauth());
      setModal(false); notify('تم حفظ الشريك'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/partners/${confirmDel.id}`, sauth()); notify('تم حذف الشريك'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-partners">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">الشركاء والرعاة ({partners.length})</h2>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="partner-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة شريك</PrimaryBtn>
      </div>
      {loading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-slate-100" />)}</div>
        : partners.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="partners-empty">لا يوجد شركاء — أضف أول شريك ليظهر في الصفحة الرئيسية</div>
        : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {partners.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="partner-card">
                <div className="flex items-center gap-3">
                  {p.logo ? <img src={logoUrl(p.logo)} alt="" className="h-11 w-11 rounded-lg bg-slate-50 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                    : <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#16233b]/10 text-sm font-bold text-[#16233b]">{(p.name || '؟').slice(0, 2)}</div>}
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-slate-900">{p.name}</div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.is_active !== false ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_active !== false ? 'ظاهر' : 'مخفي'}</span>
                  </div>
                </div>
                {p.description && <p className="mt-2 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
                {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-teal-700" dir="ltr">{p.website}</a>}
                <div className="mt-3 flex justify-end gap-1 border-t border-slate-50 pt-2">
                  <button onClick={() => openForm(p)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="partner-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                  <button onClick={() => setConfirmDel(p)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="partner-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Modal open={modal} title={editing ? 'تعديل الشريك' : 'إضافة شريك'} onClose={() => setModal(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="partner-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="اسم الشريك" required><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-input-name" /></Field>
          <Field label="الوصف"><input className={inputCls} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="الموقع الإلكتروني"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
          <Field label="شعار الشريك">
            <div className="flex items-center gap-3">
              {form.logo ? <img src={logoUrl(form.logo)} alt="" className="h-12 w-12 rounded-lg border border-slate-200 bg-slate-50 object-contain" /> : null}
              <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
                {uploading ? 'جارٍ الرفع...' : 'رفع شعار'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="partner-logo-input" />
              </label>
              {form.logo && <button type="button" onClick={() => setForm({ ...form, logo: '' })} className="text-xs font-semibold text-red-600" data-testid="partner-remove-logo-btn">إزالة</button>}
            </div>
            <input dir="ltr" placeholder="أو ألصق رابط شعار خارجي https://..." className={`${inputCls} mt-2`} value={form.logo || ''} onChange={(e) => setForm({ ...form, logo: e.target.value })} data-testid="partner-input-logo" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="الترتيب"><input type="number" min="0" className={inputCls} value={form.order_index ?? 0} onChange={(e) => setForm({ ...form, order_index: e.target.value })} /></Field>
            <label className="mt-6 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> ظاهر في الموقع</label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="partner-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف الشريك" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف «{confirmDel?.name}»؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="partner-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

// ====================== الإشعارات والرسائل ======================
export function NotificationsTab({ notify }) {
  const [data, setData] = useState({ announcements: [], messages: [], contact_messages: [] });
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, sauth());
      setData(res.data && !Array.isArray(res.data) ? res.data : { announcements: [], messages: [], contact_messages: [] });
    } catch { setData({ announcements: [], messages: [], contact_messages: [] }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const markContactHandled = async (id) => {
    try {
      await axios.put(`${API}/platform/contact-messages/${id}/mark-handled`, null, sauth());
      notify('تم تحديث حالة الرسالة');
      load();
    } catch (e2) { notify(errMsg(e2, 'فشل التحديث')); }
  };

  const sendReply = async (e) => {
    e.preventDefault(); setErr(null);
    if (!replyText.trim()) { setErr('اكتب نص الرد'); return; }
    try {
      await axios.post(`${API}/club-messages/${replying.id}/reply`, null, { ...sauth(), params: { reply_message: replyText.trim() } });
      setReplying(null); setReplyText(''); notify('تم إرسال الرد'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل إرسال الرد')); }
  };

  const fmtDT = (d) => (d ? new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(d)) : '—');

  return (
    <div className="space-y-4" data-testid="superadmin-notifications">
      <h2 className="text-lg font-bold text-slate-900">الإشعارات والتواصل</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">التعاميم المرسلة ({(data.announcements || []).length})</div>
          {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
            : (data.announcements || []).length === 0 ? <p className="p-4 text-sm text-slate-400">لا توجد تعاميم — أرسل تعميماً من تبويب نظرة عامة</p>
            : (
              <div className="max-h-96 divide-y divide-slate-50 overflow-y-auto">
                {data.announcements.map((a) => (
                  <div key={a.id} className="px-4 py-2.5" data-testid="announcement-row">
                    <div className="text-sm font-bold text-slate-800">{a.title}</div>
                    <div className="text-xs text-slate-500">{a.content || a.message}</div>
                    <div className="mt-0.5 text-[10px] text-slate-400">{fmtDT(a.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">رسائل المنشآت ({(data.messages || []).length})</div>
          {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
            : (data.messages || []).length === 0 ? <p className="p-4 text-sm text-slate-400" data-testid="messages-empty">لا توجد رسائل واردة من المنشآت</p>
            : (
              <div className="max-h-96 divide-y divide-slate-50 overflow-y-auto">
                {data.messages.map((m) => (
                  <div key={m.id} className="px-4 py-2.5" data-testid="club-message-row">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-800">{m.subject || 'رسالة'}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${m.status === 'replied' ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'}`}>{m.status === 'replied' ? 'تم الرد' : 'بانتظار الرد'}</span>
                    </div>
                    <div className="text-xs text-slate-500">{m.message || m.content}</div>
                    <div className="mt-0.5 text-[10px] text-slate-400">{m.club_name || m.sender_name || ''} · {fmtDT(m.created_at)}</div>
                    {m.reply_message && <div className="mt-1 rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-600">الرد: {m.reply_message}</div>}
                    {m.status !== 'replied' && (
                      <button onClick={() => { setReplying(m); setReplyText(''); setErr(null); }} className="mt-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-teal-300 hover:text-teal-700" data-testid="message-reply-btn">رد</button>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="platform-contact-messages-card">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">رسائل تواصل الموقع ({(data.contact_messages || []).length})</div>
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : (data.contact_messages || []).length === 0 ? <p className="p-4 text-sm text-slate-400" data-testid="contact-messages-empty">لا توجد رسائل تواصل من زوار الموقع</p>
          : (
            <div className="max-h-96 divide-y divide-slate-50 overflow-y-auto">
              {data.contact_messages.map((m) => (
                <div key={m.id} className="px-4 py-2.5" data-testid="platform-contact-message-row">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-slate-800">{m.subject}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${m.status === 'handled' ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'}`}>{m.status === 'handled' ? 'تم الاطلاع' : 'جديدة'}</span>
                  </div>
                  <div className="text-xs text-slate-500">{m.message}</div>
                  <div className="mt-0.5 text-[10px] text-slate-400" dir="ltr">{[m.name, m.email, m.phone].filter(Boolean).join(' · ')}</div>
                  <div className="mt-0.5 text-[10px] text-slate-400">{fmtDT(m.created_at)}</div>
                  {m.status !== 'handled' && (
                    <button onClick={() => markContactHandled(m.id)} className="mt-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-teal-300 hover:text-teal-700" data-testid="platform-contact-mark-handled-btn">تمييز كمُطّلع عليها</button>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>

      <Modal open={Boolean(replying)} title={`الرد على: ${replying?.subject || 'رسالة'}`} onClose={() => setReplying(null)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        <form onSubmit={sendReply} className="space-y-3">
          <Field label="نص الرد" required><textarea rows={3} className={inputCls} value={replyText} onChange={(e) => setReplyText(e.target.value)} data-testid="reply-input" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setReplying(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="reply-submit">إرسال الرد</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}


// ====================== تحديثات المنصة ======================
const UPDATE_AUDIENCE_LABELS = { all: 'الجميع', clubs: 'الأندية', academies: 'الأكاديميات', coaches: 'المدربون' };
const UPDATE_STATUS_LABELS = { draft: ['مسودة', 'bg-slate-100 text-slate-500'], published: ['منشور', 'bg-teal-50 text-teal-700'], archived: ['مؤرشف', 'bg-amber-50 text-amber-700'] };
const UPDATE_ICON_OPTIONS = [
  ['sparkles', 'نجمة لامعة'], ['trophy', 'كأس'], ['megaphone', 'مكبر صوت'], ['gift', 'هدية'], ['shield', 'درع'],
];
const emptyUpdateForm = { title: '', description: '', image_file_id: null, banner_image_file_id: null, icon: 'sparkles', badge: 'جديد', priority: 'normal', audience: 'all', status: 'draft', publish_date: '', is_pinned: false, order: 0 };

export function PlatformUpdatesTab({ notify }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUpdateForm);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/admin/platform-updates`, sauth()); setItems(Array.isArray(res.data) ? res.data : []); } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (item) => {
    setEditing(item || null);
    setForm(item ? { ...emptyUpdateForm, ...item, publish_date: item.publish_date ? item.publish_date.slice(0, 10) : '' } : emptyUpdateForm);
    setErr(null); setModal(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      setForm((f) => ({ ...f, image_file_id: res.data?.file_id }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الصورة')); }
    setUploading(false);
  };

  const uploadBannerImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingBanner(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      setForm((f) => ({ ...f, banner_image_file_id: res.data?.file_id }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الصورة')); }
    setUploadingBanner(false);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.title.trim() || !form.description.trim()) { setErr('العنوان والوصف مطلوبان'); return; }
    const payload = {
      title: form.title.trim(), description: form.description.trim(), image_file_id: form.image_file_id || null,
      banner_image_file_id: form.banner_image_file_id || null,
      icon: form.image_file_id ? null : (form.icon || 'sparkles'), badge: form.badge || 'جديد',
      priority: form.priority || 'normal',
      audience: form.audience, status: form.status, is_pinned: Boolean(form.is_pinned), order: Number(form.order) || 0,
      publish_date: form.publish_date ? new Date(form.publish_date).toISOString() : null,
    };
    try {
      if (editing) await axios.put(`${API}/admin/platform-updates/${editing.id}`, payload, sauth());
      else await axios.post(`${API}/admin/platform-updates`, payload, sauth());
      setModal(false); notify('تم حفظ التحديث بنجاح'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const setStatus = async (item, status) => {
    try { await axios.put(`${API}/admin/platform-updates/${item.id}`, { status }, sauth()); notify('تم تحديث الحالة'); load(); }
    catch (e2) { notify(errMsg(e2, 'فشلت العملية')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/admin/platform-updates/${confirmDel.id}`, sauth()); notify('تم حذف التحديث'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-platform-updates">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-slate-900">تحديثات المنصة ({items.length})</h2>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="update-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة تحديث</PrimaryBtn>
      </div>
      <p className="text-xs text-slate-500">تُعرض آخر 5 تحديثات «منشورة» تلقائياً في شاشة تسجيل الدخول، مرتبة حسب التثبيت ثم تاريخ النشر.</p>

      <div className="rounded-xl border border-slate-200 bg-white">
        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : items.length === 0 ? <p className="p-6 text-center text-sm text-slate-400" data-testid="updates-empty">لا توجد تحديثات بعد</p>
          : (
            <div className="divide-y divide-slate-50">
              {items.map((it) => (
                <div key={it.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid="update-row">
                  <div className="flex items-center gap-3">
                    {it.image_file_id ? <img src={`${API}/files/${it.image_file_id}`} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      : <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50 text-teal-600"><Icon name="bell" className="w-4 h-4" /></div>}
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-bold text-slate-900">{it.title}</span>
                        {it.is_pinned && <span className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold text-orange-600">مثبّت</span>}
                      </div>
                      <div className="text-xs text-slate-500">{UPDATE_AUDIENCE_LABELS[it.audience] || it.audience} · {fmtDate(it.publish_date)}{it.expires_at && <span className="text-amber-600"> · ينتهي {fmtDate(it.expires_at)}</span>}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${UPDATE_STATUS_LABELS[it.status]?.[1] || ''}`}>{UPDATE_STATUS_LABELS[it.status]?.[0] || it.status}</span>
                    {it.status !== 'published' && <button onClick={() => setStatus(it, 'published')} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid="update-publish-btn">نشر</button>}
                    {it.status !== 'archived' && <button onClick={() => setStatus(it, 'archived')} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="update-archive-btn">أرشفة</button>}
                    <button onClick={() => openForm(it)} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="update-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDel(it)} title="حذف" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="update-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <Modal open={modal} title={editing ? 'تعديل التحديث' : 'إضافة تحديث جديد'} onClose={() => setModal(false)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="update-form-error">{err}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="col-span-full"><Field label="العنوان" required><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="update-input-title" /></Field></div>
          <div className="col-span-full"><Field label="الوصف الكامل" required><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="update-input-description" /></Field></div>
          <div className="col-span-full rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="update-auto-link-note">
            {editing?.link ? <>رابط التحديث التلقائي: <span dir="ltr" className="font-semibold text-slate-700">{editing.link}</span></> : 'سينشئ النظام رابطاً خاصاً بهذا التحديث تلقائياً بعد الحفظ'}
          </div>
          <Field label="الشارة"><input placeholder="جديد / تحسين" className={inputCls} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="update-input-badge" /></Field>
          <Field label="الأهمية">
            <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="update-input-priority">
              <option value="normal">عادية</option>
              <option value="high">مهمة (تظهر كبطاقة تنبيه فورية)</option>
              <option value="urgent">عاجلة (تظهر كبطاقة تنبيه فورية)</option>
            </select>
          </Field>
          <Field label="الجمهور المستهدف">
            <select className={inputCls} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="update-input-audience">
              {Object.entries(UPDATE_AUDIENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="الحالة">
            <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="update-input-status">
              {Object.entries(UPDATE_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
            </select>
          </Field>
          <Field label="تاريخ النشر"><input type="date" className={inputCls} value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} data-testid="update-input-publish-date" /></Field>
          <Field label="ترتيب العرض"><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="update-input-order" /></Field>
          <div className="col-span-full flex items-center gap-2">
            <input type="checkbox" id="update-pin" checked={form.is_pinned} onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })} data-testid="update-input-pinned" />
            <label htmlFor="update-pin" className="text-sm text-slate-700">تثبيت هذا التحديث في الأعلى</label>
          </div>
          <div className="col-span-full rounded-xl border border-slate-200 p-4">
            <div className="mb-2 text-sm font-bold text-slate-700">الأيقونة الصغيرة (44×44)</div>
            <p className="mb-2 text-xs text-slate-400">تظهر داخل دائرة صغيرة أعلى البطاقة. يمكن رفع صورة مصغّرة أو اختيار أيقونة جاهزة.</p>
            <div className="flex flex-wrap items-center gap-3">
              {form.image_file_id ? <img src={`${API}/files/${form.image_file_id}`} alt="" className="h-12 w-12 rounded-lg object-cover" /> : null}
              <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
                {uploading ? 'جارٍ الرفع...' : 'رفع صورة الأيقونة'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="update-image-input" />
              </label>
              {form.image_file_id && <button type="button" onClick={() => setForm({ ...form, image_file_id: null })} className="text-xs font-semibold text-red-600" data-testid="update-remove-image-btn">إزالة الصورة</button>}
              <span className="text-xs text-slate-400">أو اختر أيقونة (تُستخدم عند عدم وجود صورة):</span>
              <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="update-input-icon">
                {UPDATE_ICON_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="col-span-full rounded-xl border border-slate-200 p-4">
            <div className="mb-2 text-sm font-bold text-slate-700">صورة التحديث الكبيرة (اختيارية)</div>
            <p className="mb-2 text-xs text-slate-400">صورة مختلفة عن الأيقونة، تُعرض بشكل بارز داخل بطاقة التحديث (مثال: لقطة من الميزة الجديدة). يُفضّل أبعاد عريضة (نحو 400×160).</p>
            <div className="flex flex-wrap items-center gap-3">
              {form.banner_image_file_id ? <img src={`${API}/files/${form.banner_image_file_id}`} alt="" className="h-16 w-28 rounded-lg object-cover" /> : null}
              <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300">
                {uploadingBanner ? 'جارٍ الرفع...' : 'رفع صورة التحديث'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadBannerImage} data-testid="update-banner-image-input" />
              </label>
              {form.banner_image_file_id && <button type="button" onClick={() => setForm({ ...form, banner_image_file_id: null })} className="text-xs font-semibold text-red-600" data-testid="update-remove-banner-image-btn">إزالة الصورة</button>}
            </div>
          </div>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="update-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف التحديث" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف تحديث «{confirmDel?.title}» نهائياً؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="update-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

// ====================== المحاسبة المالية للمنصة (Platform Accounting) ======================
const ACC_PLAN_LABELS = { free: 'مجانية', basic: 'أساسية', premium: 'مميزة', enterprise: 'مؤسسات' };
const ACC_METHOD_LABELS = { bank_transfer: 'تحويل بنكي', cash: 'نقدي', other: 'أخرى' };
const money = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ر.س`;

const ENTITY_TYPE_LABELS = { club: 'منشأة (نادي/أكاديمية)', coach: 'مدرب' };

export function AccountingTab({ notify }) {
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [printPay, setPrintPay] = useState(null);
  const [platformName, setPlatformName] = useState('تسيار');

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const [ov, pay, cl, co, plat] = await Promise.all([
        axios.get(`${API}/saas/admin/financial-overview`, sauth()),
        axios.get(`${API}/saas/admin/payments`, sauth()),
        axios.get(`${API}/saas/admin/clubs?limit=100`, sauth()),
        axios.get(`${API}/coaches/admin/list?page_size=100`, sauth()).catch(() => null),
        axios.get(`${API}/platform/settings`).catch(() => null),
      ]);
      if (plat?.data?.platform_name) setPlatformName(plat.data.platform_name);
      setOverview(ov.data);
      setPayments(Array.isArray(pay.data) ? pay.data : []);
      setClubs(Array.isArray(cl.data) ? cl.data : []);
      setCoaches(Array.isArray(co?.data?.items) ? co.data.items : []);
    } catch (e) { setLoadError(errMsg(e, 'تعذر تحميل بيانات المحاسبة')); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const coachLabel = (c) => `${c.full_name_ar || c.full_name_en || c.email} — ${c.email}`;

  const openForm = () => {
    setForm({ entity_type: 'club', club_id: clubs[0]?.id || '', coach_id: coaches[0]?.id || '', plan: 'basic', amount: '', payment_method: 'bank_transfer', billing_period_months: 1, payment_date: new Date().toISOString().slice(0, 10), notes: '' });
    setErr(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (form.entity_type === 'club' && !form.club_id) { setErr('اختر المنشأة'); return; }
    if (form.entity_type === 'coach' && !form.coach_id) { setErr('اختر المدرب'); return; }
    if (!form.amount || Number(form.amount) < 0) { setErr('أدخل مبلغاً صحيحاً'); return; }
    try {
      await axios.post(`${API}/saas/admin/payments`, {
        entity_type: form.entity_type, club_id: form.entity_type === 'club' ? form.club_id : null, coach_id: form.entity_type === 'coach' ? form.coach_id : null,
        plan: form.plan, amount: Number(form.amount), currency: 'SAR',
        payment_method: form.payment_method, billing_period_months: Number(form.billing_period_months) || 1,
        payment_date: form.payment_date, notes: form.notes || '',
      }, sauth());
      setModal(false); notify(form.entity_type === 'coach' ? 'تم تسجيل دفعة المدرب' : 'تم تسجيل الدفعة وتحديث اشتراك المنشأة'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل تسجيل الدفعة')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/saas/admin/payments/${confirmDel.id}`, sauth()); notify('تم حذف سجل الدفعة'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  if (loading && !overview) return <div className="h-48 animate-pulse rounded-xl bg-slate-100" />;
  if (loadError && !overview) return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center" data-testid="accounting-load-error">
      <p className="text-sm text-red-700">{loadError}</p>
      <button onClick={load} className="mt-3 rounded-lg border border-red-300 px-4 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100" data-testid="accounting-retry-btn">إعادة المحاولة</button>
    </div>
  );
  const maxMonthly = Math.max(1, ...(overview?.monthly_revenue || []).map((m) => m.amount));

  return (
    <div className="space-y-4" data-testid="superadmin-accounting">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">المحاسبة المالية للمنصة</h2>
        <PrimaryBtn onClick={openForm} data-testid="accounting-add-payment-btn"><Icon name="plus" className="w-4 h-4" /> تسجيل دفعة جديدة</PrimaryBtn>
      </div>

      <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
        هذه الأرقام مبنية بالكامل على الدفعات الفعلية التي تسجّلها هنا عند استلام تحويل من أي منشأة لقاء اشتراكها في تسيار. لا توجد بوابة دفع تلقائية حالياً — كل دفعة تُسجَّل يدوياً وتُحدّث حالة اشتراك المنشأة فوراً.
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatBox label="إجمالي الإيرادات" value={money(overview?.total_revenue)} testId="accounting-stat-total" />
        <StatBox label="إيرادات هذا الشهر" value={money(overview?.revenue_this_month)} testId="accounting-stat-month" />
        <StatBox label="إيرادات هذه السنة" value={money(overview?.revenue_this_year)} testId="accounting-stat-year" />
        <StatBox label="تقدير الإيراد الشهري (MRR)" value={money(overview?.mrr_estimate)} testId="accounting-stat-mrr" />
        <StatBox label="منشآت مدفوعة نشطة" value={overview?.paying_clubs_count ?? 0} testId="accounting-stat-paying-clubs" />
        <StatBox label="مدربون مدفوعون نشطون" value={overview?.paying_coaches_count ?? 0} testId="accounting-stat-paying-coaches" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-sm font-bold text-slate-800">الإيرادات الشهرية (آخر 12 شهراً)</div>
        <div className="flex h-32 items-end gap-1.5" data-testid="accounting-monthly-chart">
          {(overview?.monthly_revenue || []).map((m, i) => (
            <div key={i} className="group relative flex-1" title={`${m.month}: ${money(m.amount)}`}>
              <div className="mx-auto w-full rounded-t-sm bg-gradient-to-t from-teal-600 to-teal-400" style={{ height: `${Math.max(3, (m.amount / maxMonthly) * 100)}%` }} />
            </div>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-slate-400">
          <span>{overview?.monthly_revenue?.[0]?.month}</span>
          <span>{overview?.monthly_revenue?.[overview.monthly_revenue.length - 1]?.month}</span>
        </div>
      </div>

      {Object.keys(overview?.revenue_by_plan || {}).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 text-sm font-bold text-slate-800">الإيرادات حسب الباقة</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(overview.revenue_by_plan).map(([plan, amt]) => (
              <div key={plan} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-center" data-testid={`accounting-plan-${plan}`}>
                <div className="text-xs text-slate-500">{ACC_PLAN_LABELS[plan] || plan}</div>
                <div className="mt-1 text-base font-bold text-teal-700">{money(amt)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 p-4 text-sm font-bold text-slate-800">سجل الدفعات ({payments.length})</div>
        {payments.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="accounting-payments-empty">لا توجد دفعات مسجلة بعد</div>
        ) : (
          <table className="w-full text-sm" data-testid="accounting-payments-table">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="px-4 py-2 text-start font-semibold">العميل</th>
              <th className="px-2 py-2 font-semibold">الباقة</th>
              <th className="px-2 py-2 font-semibold">المبلغ</th>
              <th className="px-2 py-2 font-semibold">طريقة الدفع</th>
              <th className="px-2 py-2 font-semibold">تاريخ الدفعة</th>
              <th className="px-2 py-2 font-semibold">سجّلها</th>
              <th className="px-2 py-2"></th>
            </tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="accounting-payment-row">
                  <td className="px-4 py-2 font-semibold text-slate-800">
                    {p.club_name}
                    <span className={`mr-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${p.entity_type === 'coach' ? 'bg-purple-50 text-purple-700' : 'bg-slate-100 text-slate-500'}`}>{p.entity_type === 'coach' ? 'مدرب' : 'منشأة'}</span>
                  </td>
                  <td className="px-2 py-2 text-center"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{ACC_PLAN_LABELS[p.plan] || p.plan}</span></td>
                  <td className="px-2 py-2 text-center font-bold text-teal-700">{money(p.amount)}</td>
                  <td className="px-2 py-2 text-center text-xs text-slate-500">{ACC_METHOD_LABELS[p.payment_method] || p.payment_method}</td>
                  <td className="px-2 py-2 text-center text-xs text-slate-500">{fmtDate(p.payment_date)}</td>
                  <td className="px-2 py-2 text-center text-xs text-slate-400">{p.recorded_by || '—'}</td>
                  <td className="px-2 py-2 text-left">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setPrintPay(p)} title="طباعة إيصال" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="accounting-payment-receipt-btn"><Icon name="document" className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmDel(p)} title="حذف" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="accounting-payment-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={modal} title="تسجيل دفعة جديدة" onClose={() => setModal(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="accounting-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="نوع العميل" required>
            <div className="flex gap-2" data-testid="accounting-input-entity-type">
              {Object.entries(ENTITY_TYPE_LABELS).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, entity_type: k })} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${form.entity_type === k ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600'}`} data-testid={`accounting-entity-type-${k}`}>{v}</button>
              ))}
            </div>
          </Field>
          {form.entity_type === 'coach' ? (
            <Field label="المدرب" required>
              {coaches.length === 0 ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="accounting-coaches-empty">لا يوجد مدربون مسجّلون بالمنصة حالياً</p>
              ) : (
                <select className={inputCls} value={form.coach_id || ''} onChange={(e) => setForm({ ...form, coach_id: e.target.value })} data-testid="accounting-input-coach">
                  {coaches.map((c) => <option key={c.id} value={c.id}>{coachLabel(c)}</option>)}
                </select>
              )}
            </Field>
          ) : (
            <Field label="المنشأة" required>
              {clubs.length === 0 ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="accounting-clubs-empty">لا توجد منشآت متاحة حالياً</p>
              ) : (
                <select className={inputCls} value={form.club_id || ''} onChange={(e) => setForm({ ...form, club_id: e.target.value })} data-testid="accounting-input-club">
                  {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="الباقة">
              <select className={inputCls} value={form.plan || 'basic'} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="accounting-input-plan">
                {Object.entries(ACC_PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="المبلغ (ر.س)" required><input type="number" min="0" step="0.01" className={inputCls} value={form.amount ?? ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="accounting-input-amount" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="طريقة الدفع">
              <select className={inputCls} value={form.payment_method || 'bank_transfer'} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="accounting-input-method">
                {Object.entries(ACC_METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="مدة الفترة">
              <select className={inputCls} value={form.billing_period_months || 1} onChange={(e) => setForm({ ...form, billing_period_months: e.target.value })} data-testid="accounting-input-period">
                <option value={1}>شهر واحد</option>
                <option value={3}>3 أشهر</option>
                <option value={6}>6 أشهر</option>
                <option value={12}>سنة كاملة</option>
              </select>
            </Field>
          </div>
          <Field label="تاريخ الدفعة"><input type="date" className={inputCls} value={form.payment_date || ''} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="accounting-input-date" /></Field>
          <Field label="ملاحظات"><textarea rows={2} className={inputCls} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="accounting-input-notes" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="accounting-form-submit">تسجيل الدفعة</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف الدفعة" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف دفعة «{confirmDel?.club_name}» بمبلغ {money(confirmDel?.amount)}؟ (لن يُعدَّل اشتراك المنشأة تلقائياً)</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="accounting-confirm-del">حذف</button>
        </div>
      </Modal>

      {printPay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <PrintStyle />
          <div className="absolute inset-0 bg-black/50 print:hidden" onClick={() => setPrintPay(null)} />
          <div className="relative w-full max-w-md">
            <div className="print-area rounded-xl bg-white p-6 shadow-2xl" data-testid="accounting-receipt-view">
              <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <div className="text-base font-bold text-slate-900">{platformName}</div>
                  <div className="text-xs text-slate-500">إيصال دفعة اشتراك</div>
                </div>
                <div className="text-xs text-slate-400" dir="ltr">#{printPay.id?.slice(0, 8)}</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">العميل</span><span className="font-semibold text-slate-800">{printPay.club_name} {printPay.entity_type === 'coach' ? '(مدرب)' : ''}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">الباقة</span><span className="font-semibold text-slate-800">{ACC_PLAN_LABELS[printPay.plan] || printPay.plan}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">مدة الفترة</span><span className="font-semibold text-slate-800">{printPay.billing_period_months} شهر</span></div>
                <div className="flex justify-between"><span className="text-slate-500">طريقة الدفع</span><span className="font-semibold text-slate-800">{ACC_METHOD_LABELS[printPay.payment_method] || printPay.payment_method}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">تاريخ الدفعة</span><span className="font-semibold text-slate-800">{fmtDate(printPay.payment_date)}</span></div>
                {printPay.notes && <div className="flex justify-between"><span className="text-slate-500">ملاحظات</span><span className="font-semibold text-slate-800">{printPay.notes}</span></div>}
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-bold"><span>المبلغ المستلم</span><span dir="rtl" className="text-teal-700">{money(printPay.amount)}</span></div>
              </div>
              <div className="mt-4 border-t border-slate-100 pt-3 text-center text-[11px] text-slate-400">إيصال داخلي لتوثيق دفعة اشتراك منشأة على منصة {platformName} — ليس فاتورة ضريبية</div>
            </div>
            <div className="mt-3 flex justify-center gap-2 print:hidden">
              <PrimaryBtn onClick={printArea} data-testid="accounting-receipt-print-btn"><Icon name="document" className="w-4 h-4" /> طباعة الإيصال</PrimaryBtn>
              <button onClick={() => setPrintPay(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600">إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatBox = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="mt-1 text-lg font-bold text-slate-900">{value}</div>
  </div>
);

// ====================== صندوق الدعم المركزي (مذكرات الدعم) ======================
const TICKET_STATUS_LABELS = {
  open: { text: 'مفتوحة', cls: 'bg-blue-50 text-blue-700' },
  in_progress: { text: 'قيد المعالجة', cls: 'bg-amber-50 text-amber-700' },
  waiting_customer: { text: 'بانتظار رد العميل', cls: 'bg-purple-50 text-purple-700' },
  resolved: { text: 'تم الحل', cls: 'bg-emerald-50 text-emerald-700' },
  closed: { text: 'مغلقة', cls: 'bg-slate-100 text-slate-500' },
};
const TICKET_PRIORITY_LABELS = {
  low: { text: 'منخفضة', cls: 'bg-slate-100 text-slate-600' },
  medium: { text: 'متوسطة', cls: 'bg-blue-50 text-blue-700' },
  high: { text: 'عالية', cls: 'bg-orange-50 text-orange-700' },
  urgent: { text: 'عاجلة', cls: 'bg-red-50 text-red-600' },
};
const TICKET_CATEGORY_LABELS = { technical: 'تقني', billing: 'مالي', feature_request: 'طلب ميزة', general: 'عام', other: 'أخرى' };
const fmtDT2 = (d) => (d ? new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(d)) : '—');

function SupportTicketDetailModal({ ticket, onClose, onChanged, notify }) {
  const [detail, setDetail] = useState(null);
  const [staff, setStaff] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [statusVal, setStatusVal] = useState(ticket.status);
  const [assignVal, setAssignVal] = useState(ticket.assigned_to || '');

  const load = useCallback(() => {
    axios.get(`${API}/support-tickets/${ticket.id}`, sauth()).then((r) => setDetail(r.data)).catch(() => {});
  }, [ticket.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/support-tickets/assignable-staff`, sauth()).then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support-tickets/${ticket.id}/messages`, { message: reply.trim() }, sauth());
      setReply(''); load(); onChanged();
    } catch (e2) { notify(errMsg(e2, 'فشل إرسال الرد')); }
    setSending(false);
  };

  const saveStatus = async () => {
    try {
      const staffMember = staff.find((s) => s.id === assignVal);
      await axios.put(`${API}/support-tickets/${ticket.id}/status`, {
        status: statusVal, assigned_to: assignVal || null, assigned_to_name: staffMember?.name || null,
      }, sauth());
      notify('تم تحديث التذكرة'); load(); onChanged();
    } catch (e2) { notify(errMsg(e2, 'فشل التحديث')); }
  };

  return (
    <Modal open title={detail ? `${detail.ticket.ticket_number} — ${detail.ticket.subject}` : `تذكرة ${ticket.ticket_number}`} onClose={onClose} wide>
      {!detail ? <div className="h-40 animate-pulse rounded-lg bg-slate-100" /> : (
        <div className="space-y-4" data-testid="support-admin-ticket-detail">
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-800">{detail.ticket.club_name}</span>
              <span className="text-xs text-slate-400">{TICKET_CATEGORY_LABELS[detail.ticket.category] || detail.ticket.category} · {fmtDT2(detail.ticket.created_at)}</span>
            </div>
            <p className="mt-1 whitespace-pre-line text-slate-600">{detail.ticket.description}</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="الحالة">
              <select className={inputCls} value={statusVal} onChange={(e) => setStatusVal(e.target.value)} data-testid="support-admin-status-select">
                {Object.entries(TICKET_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
              </select>
            </Field>
            <Field label="تعيين مسؤول">
              <select className={inputCls} value={assignVal} onChange={(e) => setAssignVal(e.target.value)} data-testid="support-admin-assign-select">
                <option value="">بلا تعيين</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex justify-end"><PrimaryBtn onClick={saveStatus} data-testid="support-admin-save-status-btn">حفظ الحالة والتعيين</PrimaryBtn></div>

          <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-slate-100 p-3" data-testid="support-admin-messages">
            {detail.messages.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">لا توجد ردود بعد</p> : detail.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.sender_type === 'admin' ? 'bg-[#16233b] text-white' : 'bg-slate-100 text-slate-800'}`}>
                  <p className="whitespace-pre-line">{m.message}</p>
                  <p className="mt-1 text-[10px] opacity-70">{m.sender_name} · {fmtDT2(m.created_at)}</p>
                </div>
              </div>
            ))}
          </div>

          {detail.ticket.rating && (
            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm" data-testid="support-admin-rating-box">
              <div className="flex items-center gap-1">{[1, 2, 3, 4, 5].map((n) => <StarIcon key={n} className={`h-4 w-4 ${n <= detail.ticket.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />)}</div>
              {detail.ticket.rating_comment && <p className="mt-1 text-slate-600">{detail.ticket.rating_comment}</p>}
            </div>
          )}

          <div className="flex items-end gap-2">
            <textarea rows={2} className={`${inputCls} flex-1`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="اكتب رداً للمنشأة..." data-testid="support-admin-reply-input" />
            <PrimaryBtn onClick={sendReply} disabled={sending || !reply.trim()} data-testid="support-admin-reply-submit">{sending ? '...' : 'إرسال'}</PrimaryBtn>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SupportInboxTab({ notify }) {
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', club_id: '', search: '' });
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.club_id) params.club_id = filters.club_id;
    if (filters.search) params.search = filters.search;
    try {
      const [sRes, tRes] = await Promise.all([
        axios.get(`${API}/support-tickets/summary`, sauth()),
        axios.get(`${API}/support-tickets`, { ...sauth(), params }),
      ]);
      setSummary(sRes.data || {});
      setTickets(Array.isArray(tRes.data) ? tRes.data : []);
    } catch { setTickets([]); }
    setLoading(false);
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/saas/admin/clubs?limit=200`, sauth()).then((r) => setClubs(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const chips = [
    ['', 'الكل', summary?.total],
    ['open', 'مفتوحة', summary?.open],
    ['in_progress', 'قيد المعالجة', summary?.in_progress],
    ['waiting_customer', 'بانتظار العميل', summary?.waiting_customer],
    ['resolved', 'تم الحل', summary?.resolved],
    ['closed', 'مغلقة', summary?.closed],
  ];

  return (
    <div className="space-y-4" data-testid="superadmin-support-inbox">
      <h2 className="text-lg font-bold text-slate-900">صندوق الدعم المركزي</h2>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="support-inbox-summary">
        {chips.map(([key, label, value]) => (
          <button
            key={key}
            onClick={() => setFilters((f) => ({ ...f, status: key }))}
            data-testid={`support-inbox-summary-${key || 'all'}`}
            className={`rounded-xl border p-3 text-right transition-colors ${filters.status === key ? 'border-[#16233b] bg-[#16233b] text-white' : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300'}`}
          >
            <div className={`text-xs ${filters.status === key ? 'text-slate-300' : 'text-slate-500'}`}>{label}</div>
            <div className="mt-1 text-xl font-bold">{value ?? '—'}</div>
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="بحث برقم التذكرة أو العنوان أو المنشأة..." className="w-56 bg-transparent text-sm outline-none" data-testid="support-inbox-search" />
          </div>
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="support-inbox-priority-filter">
            <option value="">كل الأولويات</option>
            {Object.entries(TICKET_PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
          </select>
          <select value={filters.club_id} onChange={(e) => setFilters({ ...filters, club_id: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="support-inbox-club-filter">
            <option value="">كل المنشآت</option>
            {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="mr-auto text-xs text-slate-400">{tickets.length} تذكرة</span>
        </div>

        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : tickets.length === 0 ? <p className="p-6 text-center text-sm text-slate-400" data-testid="support-inbox-empty">لا توجد تذاكر مطابقة</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2 font-semibold">التذكرة</th>
                  <th className="px-2 py-2 font-semibold">المنشأة</th>
                  <th className="px-2 py-2 font-semibold">الأولوية</th>
                  <th className="px-2 py-2 font-semibold">الحالة</th>
                  <th className="px-2 py-2 font-semibold">المسؤول</th>
                  <th className="px-2 py-2 font-semibold">آخر تحديث</th>
                </tr></thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id} onClick={() => setViewing(t)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50/60" data-testid="support-inbox-ticket-row">
                      <td className="px-4 py-2">
                        <div className="font-mono text-xs text-slate-400" dir="ltr">{t.ticket_number}</div>
                        <div className="font-semibold text-slate-800">{t.subject}</div>
                      </td>
                      <td className="px-2 py-2 text-slate-600">{t.club_name}</td>
                      <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TICKET_PRIORITY_LABELS[t.priority]?.cls}`}>{TICKET_PRIORITY_LABELS[t.priority]?.text}</span></td>
                      <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TICKET_STATUS_LABELS[t.status]?.cls}`}>{TICKET_STATUS_LABELS[t.status]?.text}</span></td>
                      <td className="px-2 py-2 text-xs text-slate-500">{t.assigned_to_name || '—'}</td>
                      <td className="px-2 py-2 text-xs text-slate-500">{fmtDT2(t.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {viewing && (
        <SupportTicketDetailModal
          ticket={viewing}
          onClose={() => setViewing(null)}
          onChanged={load}
          notify={notify}
        />
      )}
    </div>
  );
}


// ====================== عروض أسعار المنصة (للمشتركين والأكاديميات الجديدة) ======================
const DISCOUNT_TYPES_SA = { '': 'بلا خصم', percentage: 'نسبة %', fixed: 'مبلغ ثابت' };
const emptySAItem = () => ({ description: '', quantity: 1, unit: '', unit_price: '' });
const cleanItemTitle = (desc) => (desc || '').replace(/^باقة\s+/, '').trim();
const emptySAForm = () => ({
  recipientMode: 'subscriber', recipient_club_id: '', recipient_name: '', recipient_phone: '', recipient_email: '', recipient_label: '',
  title: '', issue_date: new Date().toISOString().slice(0, 10), expiry_date: '',
  items: [emptySAItem()], discount_type: '', discount_value: '',
  tax_enabled: false, tax_rate: 15, notes: '', terms: '', status: 'draft', linked_plan_id: '',
});

const computeSATotals = (form) => {
  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const discountValue = parseFloat(form.discount_value) || 0;
  let discountAmount = 0;
  if (form.discount_type === 'percentage') discountAmount = subtotal * (discountValue / 100);
  else if (form.discount_type === 'fixed') discountAmount = Math.min(discountValue, subtotal);
  const taxable = subtotal - discountAmount;
  const taxRate = form.tax_enabled ? (parseFloat(form.tax_rate) || 0) : 0;
  const taxAmount = form.tax_enabled ? taxable * (taxRate / 100) : 0;
  return { subtotal, discountAmount, taxable, taxAmount, total: taxable + taxAmount };
};

// محتوى عرض السعر القابل لإعادة الاستخدام — يُعرض مرة للمعاينة على الشاشة ومرة داخل portal مخصص للطباعة فقط
function QuoteDocumentBody({ viewQuote, platform, platformLogoUrl }) {
  return (
    <>
      <div className="mb-6 flex items-center justify-between border-b-2 border-teal-600 pb-5">
        <div className="flex items-center gap-3">
          {platformLogoUrl ? <img src={platformLogoUrl} alt={platform.platform_name || 'تسيار'} className="h-14 w-14 rounded-xl object-contain" /> : <Icon name="grid" className="h-12 w-12 text-slate-300" />}
          <div>
            <div className="text-lg font-extrabold text-[#16233B]">{platform.platform_name || 'تسيار'}</div>
            <div className="text-xs text-slate-500">منصة تسيار لإدارة الأنشطة الرياضية</div>
          </div>
        </div>
        <div className="text-left">
          <div className="text-2xl font-black text-slate-900" data-testid="saquote-print-title">عرض سعر</div>
          <div className="text-[11px] font-medium text-slate-400">Price Quote</div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-4 gap-3 rounded-xl border border-slate-200/80 bg-slate-50 p-4" data-testid="saquote-meta-card">
        <div className="flex flex-col gap-1 rounded-lg bg-white px-3 py-2 shadow-sm">
          <span className="text-[11px] font-medium text-slate-500">رقم العرض</span>
          <span className="truncate text-sm font-bold text-slate-800" dir="ltr" data-testid="saquote-meta-number">{viewQuote.quote_number}</span>
        </div>
        <div className="flex flex-col gap-1 rounded-lg bg-white px-3 py-2 shadow-sm">
          <span className="text-[11px] font-medium text-slate-500">تاريخ الإصدار</span>
          <span className="truncate text-sm font-bold text-slate-800" data-testid="saquote-meta-issue-date">{fmtDate(viewQuote.issue_date)}</span>
        </div>
        <div className="flex flex-col gap-1 rounded-lg bg-white px-3 py-2 shadow-sm">
          <span className="text-[11px] font-medium text-slate-500">صالح حتى</span>
          <span className="truncate text-sm font-bold text-slate-800" data-testid="saquote-meta-expiry-date">{fmtDate(viewQuote.expiry_date)}</span>
        </div>
        <div className="flex min-w-0 flex-col items-start justify-center gap-1 rounded-lg bg-white px-3 py-2 shadow-sm" data-testid="saquote-meta-status-badge">
          <span className="text-[11px] font-medium text-slate-500">الحالة</span>
          <StatusBadge status={viewQuote.effective_status} />
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-teal-100 bg-teal-50/50 p-4">
        <span className="mb-2 inline-block rounded-md bg-teal-100/80 px-2.5 py-1 text-xs font-bold text-teal-800">الجهة المستفيدة</span>
        <div className="text-base font-bold text-slate-900" data-testid="saquote-client-name">{viewQuote.recipient_name}</div>
        <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
          {viewQuote.recipient_phone && <span dir="ltr" data-testid="saquote-client-phone">{viewQuote.recipient_phone}</span>}
          {viewQuote.recipient_email && <span dir="ltr" data-testid="saquote-client-email">{viewQuote.recipient_email}</span>}
          {viewQuote.recipient_label && <span className="text-slate-400">{viewQuote.recipient_label}</span>}
        </div>
      </div>

      {viewQuote.title && <h3 className="mb-3 text-sm font-bold text-slate-700">{viewQuote.title}</h3>}

      <table className="mb-6 w-full border-collapse overflow-hidden rounded-lg border border-slate-200 text-sm" data-testid="saquote-items-table">
        <thead>
          <tr className="bg-[#16233B] text-right text-xs font-bold uppercase text-white">
            <th className="px-3 py-2.5">البند / الخدمة</th><th className="px-3 py-2.5">المدة</th><th className="px-3 py-2.5">الكمية</th>
            <th className="px-3 py-2.5">سعر الوحدة</th><th className="px-3 py-2.5">الإجمالي</th>
          </tr>
        </thead>
        <tbody>
          {(viewQuote.items || []).map((it, i) => (
            <tr key={i} className={i % 2 === 1 ? 'bg-slate-50' : 'bg-white'} data-testid="saquote-item-row">
              <td className="border-t border-slate-100 px-3 py-2.5 font-medium text-slate-800">{cleanItemTitle(it.description)}</td>
              <td className="border-t border-slate-100 px-3 py-2.5 text-slate-600">{it.unit || '—'}</td>
              <td className="border-t border-slate-100 px-3 py-2.5 text-slate-600">{it.quantity}</td>
              <td className="border-t border-slate-100 px-3 py-2.5 text-slate-600" dir="rtl">{money(it.unit_price)}</td>
              <td className="border-t border-slate-100 px-3 py-2.5 font-bold text-slate-900" dir="rtl">{money(it.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mb-6 flex justify-end">
        <div className="w-full max-w-sm space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex justify-between text-sm"><span className="font-medium text-slate-600">السعر قبل الخصم</span><span className="font-bold text-slate-900" dir="rtl" data-testid="saquote-summary-subtotal">{money(viewQuote.subtotal)}</span></div>
          {viewQuote.discount_amount > 0 && (
            <div className="flex justify-between rounded-lg bg-amber-100 px-2 py-1 text-sm">
              <span className="font-medium text-amber-700">الخصم {viewQuote.discount_type === 'percentage' ? `(${viewQuote.discount_value}%)` : ''}</span>
              <span className="font-bold text-amber-700" dir="rtl" data-testid="saquote-summary-discount">- {money(viewQuote.discount_amount)}</span>
            </div>
          )}
          {viewQuote.tax_enabled && <div className="flex justify-between text-sm"><span className="font-medium text-slate-600">الضريبة ({viewQuote.tax_rate}%)</span><span className="font-bold text-slate-900" dir="rtl">{money(viewQuote.tax_amount)}</span></div>}
          <div className="flex justify-between rounded-lg border border-teal-600 bg-[#E6FFFA] px-3 py-2.5">
            <span className="text-base font-extrabold text-teal-900">الإجمالي النهائي</span>
            <span className="text-xl font-black text-teal-900" dir="rtl" data-testid="saquote-summary-total">{money(viewQuote.total)}</span>
          </div>
        </div>
      </div>

      {(viewQuote.notes || viewQuote.terms) && (
        <div className="mb-6 space-y-2">
          {viewQuote.notes && <p className="text-xs leading-relaxed text-slate-500"><span className="font-bold text-slate-600">ملاحظات: </span>{viewQuote.notes}</p>}
          {viewQuote.terms && <p className="text-xs leading-relaxed text-slate-500"><span className="font-bold text-slate-600">الشروط والأحكام: </span>{viewQuote.terms}</p>}
        </div>
      )}

      <div className="mt-auto space-y-2 border-t border-slate-200 pt-4 text-center" data-testid="report-footer" data-show-page-number="0">
        {(platform.contact_phone || platform.contact_email) && (
          <div className="text-xs text-slate-500" dir="ltr">{[platform.contact_phone, platform.contact_email].filter(Boolean).join('  —  ')}</div>
        )}
        <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold leading-relaxed text-amber-800" data-testid="saquote-print-disclaimer">
          تنبيه: هذا المستند عبارة عن عرض سعر فقط من {platform.platform_name || 'تسيار'}، ولا يُعد فاتورة ضريبية أو سند إثبات سداد، وتكون صلاحيته حتى تاريخ {fmtDate(viewQuote.expiry_date)}.
        </div>
      </div>
    </>
  );
}

export function QuotesTab({ notify }) {
  const printRef = useRef(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [clubs, setClubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [platform, setPlatform] = useState({});
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySAForm());
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewQuote, setViewQuote] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [imgExporting, setImgExporting] = useState(false);
  const [delQuote, setDelQuote] = useState(null);
  const [waTarget, setWaTarget] = useState(null);
  const [waText, setWaText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/saas/admin/quotes?page=${page}&limit=15${statusFilter ? `&status=${statusFilter}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`, sauth());
      setData(res.data);
    } catch { setData({ items: [], total: 0 }); }
    setLoading(false);
  }, [page, statusFilter, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/saas/admin/clubs?limit=200`, sauth()).then((r) => setClubs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/saas/admin/plans`, sauth()).then((r) => setPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/platform/settings`).then((r) => setPlatform(r.data || {})).catch(() => {});
  }, []);

  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const totals = computeSATotals(form);

  const openCreate = () => { setEditing(null); setForm(emptySAForm()); setFormError(null); setModal(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      recipientMode: q.recipient_club_id ? 'subscriber' : 'new',
      recipient_club_id: q.recipient_club_id || '', recipient_name: q.recipient_name || '', recipient_phone: q.recipient_phone || '',
      recipient_email: q.recipient_email || '', recipient_label: q.recipient_label || '',
      title: q.title, issue_date: q.issue_date, expiry_date: q.expiry_date,
      items: (q.items || []).map((i) => ({ ...i })), discount_type: q.discount_type || '', discount_value: q.discount_value || '',
      tax_enabled: !!q.tax_enabled, tax_rate: q.tax_rate || 15, notes: q.notes || '', terms: q.terms || '',
      status: q.status, linked_plan_id: q.linked_plan_id || '',
    });
    setFormError(null); setModal(true);
  };

  const pickClub = (clubId) => {
    const c = clubs.find((x) => x.id === clubId);
    setForm({ ...form, recipient_club_id: clubId, recipient_name: c?.name || '', recipient_phone: c?.phone || '', recipient_email: c?.email || '', recipient_label: c?.org_type === 'academy' ? 'أكاديمية مشترِكة' : 'منشأة مشترِكة' });
  };

  const applyPlan = (planId) => {
    const p = plans.find((x) => x.plan_id === planId);
    if (!p) { setForm({ ...form, linked_plan_id: '' }); return; }
    const item = { description: `باقة ${p.display_name} (${p.billing_period === 'yearly' ? 'سنوية' : 'شهرية'})`, quantity: 1, unit: p.billing_period === 'yearly' ? 'سنة' : 'شهر', unit_price: p.price };
    const hadOnlyEmpty = form.items.length === 1 && !form.items[0].description;
    setForm({ ...form, linked_plan_id: planId, items: hadOnlyEmpty ? [item] : [...form.items, item] });
  };

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.filter((i) => i.description && i.unit_price !== '');
    if (!form.recipient_name || !form.title || !form.issue_date || !form.expiry_date || items.length === 0) {
      setFormError('يرجى تعبئة اسم الجهة والعنوان والتواريخ وبند واحد على الأقل'); return;
    }
    if (form.expiry_date < form.issue_date) { setFormError('تاريخ الانتهاء يجب أن يكون بعد تاريخ الإصدار'); return; }
    setSaving(true); setFormError(null);
    const payload = {
      recipient_club_id: form.recipientMode === 'subscriber' ? (form.recipient_club_id || null) : null,
      recipient_name: form.recipient_name, recipient_phone: form.recipient_phone || null, recipient_email: form.recipient_email || null,
      recipient_label: form.recipient_label || null,
      title: form.title, issue_date: form.issue_date, expiry_date: form.expiry_date,
      items: items.map((i) => ({ description: i.description, quantity: parseFloat(i.quantity) || 1, unit: i.unit || null, unit_price: parseFloat(i.unit_price) })),
      discount_type: form.discount_type || null, discount_value: parseFloat(form.discount_value) || 0,
      tax_enabled: form.tax_enabled, tax_rate: form.tax_enabled ? (parseFloat(form.tax_rate) || 0) : null,
      notes: form.notes || null, terms: form.terms || null, linked_plan_id: form.linked_plan_id || null,
    };
    try {
      if (editing) await axios.put(`${API}/saas/admin/quotes/${editing.id}`, { ...payload, status: form.status }, sauth());
      else await axios.post(`${API}/saas/admin/quotes`, payload, sauth());
      setModal(false); notify(editing ? 'تم تحديث عرض السعر' : 'تم إنشاء عرض السعر'); load();
    } catch (err) { setFormError(errMsg(err, 'فشل الحفظ')); }
    setSaving(false);
  };

  const duplicate = async (q) => {
    try { await axios.post(`${API}/saas/admin/quotes/${q.id}/duplicate`, {}, sauth()); notify('تم نسخ عرض السعر'); load(); }
    catch (err) { notify(errMsg(err, 'فشل النسخ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/saas/admin/quotes/${delQuote.id}`, sauth()); notify('تم حذف عرض السعر'); setDelQuote(null); load(); }
    catch (err) { notify(errMsg(err, 'فشل الحذف')); setDelQuote(null); }
  };

  const openWhatsApp = (q) => {
    setWaTarget(q);
    setWaText(`مرحباً ${q.recipient_name}،\nأرسل لكم ${platform.platform_name || 'تسيار'} عرض سعر "${q.title}" برقم ${q.quote_number} بإجمالي ${money(q.total)}، ساري حتى ${fmtDate(q.expiry_date)}.\nيرجى مراجعة نسخة PDF المرسلة لكم للتفاصيل الكاملة.`);
  };
  const sendWhatsApp = () => {
    const phone = normalizeWhatsAppNumber(waTarget.recipient_phone);
    if (!phone) { notify('لا يوجد رقم جوال لهذه الجهة'); return; }
    window.open(buildWaMeLink(phone, waText), '_blank');
    setWaTarget(null);
  };

  const platformLogoUrl = platform.platform_logo ? `${API}/files/${platform.platform_logo}` : null;

  return (
    <div className="space-y-4" data-testid="superadmin-quotes">
      <PageHead title="عروض الأسعار" desc="عروض أسعار تجارية لمشتركيك الحاليين أو لأكاديميات/أندية جديدة — مستقلة تماماً عن الاشتراكات الفعلية والفواتير"
        action={<PrimaryBtn onClick={openCreate} data-testid="saquote-add-btn"><Icon name="plus" className="w-4 h-4" /> عرض سعر جديد</PrimaryBtn>} />

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="بحث باسم الجهة..." className="w-48 bg-transparent text-sm outline-none" data-testid="saquote-search" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-600" data-testid="saquote-status-filter">
            <option value="">كل الحالات</option>
            <option value="draft">مسودة</option><option value="sent">مُرسل</option><option value="accepted">مقبول</option><option value="rejected">مرفوض</option><option value="expired">منتهي</option>
          </select>
          <span className="mr-auto text-xs text-slate-400">الإجمالي: {data.total} عرض</span>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : data.items.length === 0 ? <div className="p-6"><FinanceEmpty text="لا توجد عروض أسعار — أنشئ أول عرض سعر لمشترك أو أكاديمية جديدة" /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">الرقم</th><th className="px-4 py-2.5 font-semibold">الجهة</th>
                  <th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الإصدار</th><th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الانتهاء</th>
                  <th className="px-4 py-2.5 font-semibold">الإجمالي</th><th className="px-4 py-2.5 font-semibold">الحالة</th><th className="px-4 py-2.5 font-semibold">إجراءات</th>
                </tr></thead>
                <tbody>
                  {data.items.map((q) => (
                    <tr key={q.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="saquote-row">
                      <td className="px-4 py-2.5 font-bold text-slate-800" dir="ltr">{q.quote_number}</td>
                      <td className="px-4 py-2.5 text-slate-700">
                        <div>{q.recipient_name}</div>
                        {q.recipient_label && <div className="text-xs text-slate-400">{q.recipient_label}</div>}
                      </td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(q.issue_date)}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(q.expiry_date)}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-900" dir="rtl">{money(q.total)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={q.effective_status} /></td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setViewQuote(q)} title="عرض" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="saquote-view-btn"><Icon name="document" className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(q)} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="saquote-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                          <button onClick={() => duplicate(q)} title="نسخ" className="rounded-lg p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700" data-testid="saquote-duplicate-btn"><Icon name="copy" className="w-4 h-4" /></button>
                          {q.recipient_phone && <button onClick={() => openWhatsApp(q)} title="إرسال واتساب" className="rounded-lg p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600" data-testid="saquote-whatsapp-btn"><Icon name="whatsapp" className="w-4 h-4" /></button>}
                          <button onClick={() => setDelQuote(q)} title="حذف" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="saquote-delete-btn"><Icon name="trash" className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <Paginator page={page} total={data.total} limit={15} onChange={setPage} />
      </div>

      <Modal open={modal} title={editing ? `تعديل عرض السعر ${editing.quote_number}` : 'عرض سعر جديد'} onClose={() => setModal(false)} wide>
        {formError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="saquote-form-error">{formError}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="saquote-recipient-mode">
            <button type="button" onClick={() => setForm({ ...form, recipientMode: 'subscriber' })} className={`flex-1 rounded-md py-1.5 text-xs font-semibold ${form.recipientMode === 'subscriber' ? 'bg-white shadow text-[#16233b]' : 'text-slate-500'}`} data-testid="saquote-mode-subscriber">من المشتركين الحاليين</button>
            <button type="button" onClick={() => setForm({ ...form, recipientMode: 'new', recipient_club_id: '' })} className={`flex-1 rounded-md py-1.5 text-xs font-semibold ${form.recipientMode === 'new' ? 'bg-white shadow text-[#16233b]' : 'text-slate-500'}`} data-testid="saquote-mode-new">جهة جديدة (أكاديمية/نادٍ)</button>
          </div>

          {form.recipientMode === 'subscriber' ? (
            <Field label="اختر المشترك" required>
              <select className={inputCls} value={form.recipient_club_id} onChange={(e) => pickClub(e.target.value)} data-testid="saquote-input-club-select">
                <option value="">اختر منشأة/أكاديمية...</option>
                {clubs.map((c) => <option key={c.id} value={c.id}>{c.name} {c.org_type === 'academy' ? '(أكاديمية)' : ''}</option>)}
              </select>
            </Field>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="اسم الجهة" required><input className={inputCls} value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="saquote-input-recipient-name" /></Field>
            <Field label="عنوان عرض السعر" required><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="saquote-input-title" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="جوال الجهة"><input className={inputCls} dir="ltr" value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} data-testid="saquote-input-recipient-phone" /></Field>
            <Field label="بريد الجهة"><input className={inputCls} dir="ltr" type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="saquote-input-recipient-email" /></Field>
            <Field label="تصنيف (اختياري)"><input className={inputCls} placeholder="أكاديمية جديدة، شريك..." value={form.recipient_label} onChange={(e) => setForm({ ...form, recipient_label: e.target.value })} data-testid="saquote-input-recipient-label" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="تاريخ الإصدار" required><input type="date" className={inputCls} value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="saquote-input-issue" /></Field>
            <Field label="تاريخ انتهاء الصلاحية" required><input type="date" className={inputCls} value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="saquote-input-expiry" /></Field>
            {editing && (
              <Field label="الحالة">
                <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="saquote-input-status">
                  <option value="draft">مسودة</option><option value="sent">مُرسل</option><option value="accepted">مقبول</option><option value="rejected">مرفوض</option>
                </select>
              </Field>
            )}
          </div>

          <Field label="ابدأ من باقة جاهزة (اختياري)">
            <select className={inputCls} value={form.linked_plan_id} onChange={(e) => applyPlan(e.target.value)} data-testid="saquote-input-plan-select">
              <option value="">بلا باقة — بند حر بالكامل</option>
              {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.display_name} — {money(p.price)} / {p.billing_period === 'yearly' ? 'سنوياً' : 'شهرياً'}</option>)}
            </select>
          </Field>

          <div className="space-y-2">
            <span className="text-xs font-semibold text-slate-600">البنود</span>
            {form.items.map((it, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input className={`${inputCls} min-w-[160px] flex-1`} placeholder="الوصف" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} data-testid={`saquote-item-desc-${i}`} />
                <input className={`${inputCls} w-20`} placeholder="الوحدة" value={it.unit} onChange={(e) => setItem(i, 'unit', e.target.value)} data-testid={`saquote-item-unit-${i}`} />
                <input className={`${inputCls} w-20`} dir="ltr" type="number" min="0.01" step="0.01" placeholder="كمية" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} data-testid={`saquote-item-qty-${i}`} />
                <input className={`${inputCls} w-28`} dir="ltr" type="number" min="0" step="0.01" placeholder="سعر الوحدة" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} data-testid={`saquote-item-price-${i}`} />
                <span className="w-24 text-sm font-semibold text-slate-700" dir="rtl">{money((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0))}</span>
                {form.items.length > 1 && <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="text-red-500" data-testid={`saquote-item-remove-${i}`}><Icon name="x" className="w-4 h-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, items: [...form.items, emptySAItem()] })} className="text-xs font-semibold text-teal-700" data-testid="saquote-add-item">+ إضافة بند</button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex items-end gap-2">
              <Field label="الخصم">
                <select className={inputCls} value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="saquote-input-discount-type">
                  {Object.entries(DISCOUNT_TYPES_SA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              {form.discount_type && (
                <input className={`${inputCls} w-28`} dir="ltr" type="number" min="0" step="0.01" placeholder={form.discount_type === 'percentage' ? '%' : 'ر.س'} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="saquote-input-discount-value" />
              )}
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.tax_enabled} onChange={(e) => setForm({ ...form, tax_enabled: e.target.checked })} data-testid="saquote-input-tax-enabled" />
                تطبيق الضريبة
              </label>
              {form.tax_enabled && (
                <input className={`${inputCls} w-24`} dir="ltr" type="number" min="0" max="100" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="saquote-input-tax-rate" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="ملاحظات"><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="saquote-input-notes" /></Field>
            <Field label="الشروط والأحكام"><textarea rows={2} className={inputCls} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="saquote-input-terms" /></Field>
          </div>

          <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm" data-testid="saquote-totals">
            <div className="flex justify-between"><span className="text-slate-500">المجموع</span><span dir="rtl">{money(totals.subtotal)}</span></div>
            {totals.discountAmount > 0 && <div className="flex justify-between text-amber-700"><span>الخصم</span><span dir="rtl">- {money(totals.discountAmount)}</span></div>}
            {form.tax_enabled && <div className="flex justify-between"><span className="text-slate-500">الضريبة ({form.tax_rate || 0}%)</span><span dir="rtl">{money(totals.taxAmount)}</span></div>}
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-bold"><span>الإجمالي النهائي</span><span dir="rtl">{money(totals.total)}</span></div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={saving} data-testid="saquote-form-submit">{saving ? 'جارٍ الحفظ...' : editing ? 'حفظ التعديلات' : 'إنشاء عرض السعر'}</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(delQuote)} title={`حذف عرض السعر ${delQuote?.quote_number || ''}`} onClose={() => setDelQuote(null)}>
        <p className="text-sm text-slate-600">هل أنت متأكد من حذف عرض السعر «{delQuote?.title}»؟ لا يمكن التراجع عن هذا الإجراء.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setDelQuote(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700" data-testid="saquote-delete-confirm">تأكيد الحذف</button>
        </div>
      </Modal>

      <Modal open={Boolean(waTarget)} title={`إرسال عبر واتساب — ${waTarget?.recipient_name || ''}`} onClose={() => setWaTarget(null)}>
        <Field label="نص الرسالة"><textarea rows={5} className={inputCls} value={waText} onChange={(e) => setWaText(e.target.value)} data-testid="saquote-wa-text" /></Field>
        <p className="mt-2 text-xs text-amber-700">واتساب لا يدعم إرفاق ملف تلقائياً عبر الرابط — نزّل PDF العرض من زر "عرض" أولاً وأرفقه يدوياً بعد فتح المحادثة.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setWaTarget(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={sendWhatsApp} className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="saquote-wa-send-confirm"><Icon name="whatsapp" className="w-4 h-4" /> فتح واتساب</button>
        </div>
      </Modal>

      {viewQuote && (
        <div className="saquote-view-modal fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <style>{`
            /* يتجاوز قاعدة App.css العامة (.fixed.inset-0 { overflow:hidden !important; align-items:center !important; }) التي كانت تمنع التمرير هنا فقط */
            .fixed.inset-0.saquote-view-modal { align-items: flex-start !important; overflow-y: auto !important; }
          `}</style>
          <div className="absolute inset-0" onClick={() => setViewQuote(null)} />
          <div className="relative w-full max-w-[850px] py-6">
            <div
              className="mx-auto flex flex-col rounded-2xl bg-white p-10 shadow-2xl"
              data-testid="saquote-print-view" ref={printRef} dir="rtl"
              style={{ fontFamily: "'Cairo','IBM Plex Sans Arabic',sans-serif", maxWidth: '794px' }}
            >
              <QuoteDocumentBody viewQuote={viewQuote} platform={platform} platformLogoUrl={platformLogoUrl} />
            </div>
            <div className="mt-3 flex justify-center gap-2">
              <PrimaryBtn onClick={printArea} data-testid="saquote-do-print"><Icon name="document" className="w-4 h-4" /> طباعة</PrimaryBtn>
              <button onClick={async () => { setPdfExporting(true); try { await exportPdf(printRef.current, `عرض-سعر-${viewQuote.quote_number}`, { looseRowBreak: true }); } finally { setPdfExporting(false); } }} disabled={pdfExporting} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-50" data-testid="saquote-download-pdf-btn">{pdfExporting ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="download" className="w-4 h-4" /> تنزيل PDF</>}</button>
              <button onClick={async () => { setImgExporting(true); try { await exportImage(printRef.current, `عرض-سعر-${viewQuote.quote_number}`); } finally { setImgExporting(false); } }} disabled={imgExporting} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-50" data-testid="saquote-download-image-btn">{imgExporting ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="image" className="w-4 h-4" /> تنزيل كصورة</>}</button>
              <button onClick={() => setViewQuote(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600">إغلاق</button>
            </div>
          </div>
          {createPortal(
            <div className="quote-print-portal" dir="rtl" style={{ fontFamily: "'Cairo','IBM Plex Sans Arabic',sans-serif" }} data-testid="saquote-print-portal">
              <style>{`
                .quote-print-portal { display: none; }
                @media print {
                  @page { size: A4; margin: 12mm; }
                  body > *:not(.quote-print-portal) { display: none !important; }
                  .quote-print-portal { display: flex !important; flex-direction: column; background: #fff; padding: 24px; }
                  .quote-print-portal, .quote-print-portal * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                  }
                  .quote-print-portal table { width: 100%; border-collapse: collapse; }
                  .quote-print-portal thead { display: table-header-group; }
                  .quote-print-portal tfoot { display: table-footer-group; }
                  .quote-print-portal tr { break-inside: avoid; page-break-inside: avoid; }
                  .quote-print-portal img { break-inside: avoid; page-break-inside: avoid; }
                }
              `}</style>
              <QuoteDocumentBody viewQuote={viewQuote} platform={platform} platformLogoUrl={platformLogoUrl} />
            </div>,
            document.body
          )}
        </div>
      )}
    </div>
  );
}
